// src/components/printing/PrintOrderInterface.jsx
// Interface for designing and downloading print-ready QR code materials

import React, { useState, useEffect } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../../config/firebase-config';
import { Printer, CreditCard, Package, Clock, CheckCircle, Palette, Eye, Download, MapPin, ExternalLink } from 'lucide-react';
import { RequestASongLogo } from '../common/RequestASongLogo.jsx';
import QRCode from 'qrcode';
import logoSymbol from '../../assets/requestasong-symbol.png';

const PrintOrderInterface = ({ user, displayName }) => {
  const [products, setProducts] = useState({ businessCards: [], flyers: [] });
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [quantity, setQuantity] = useState(100);
  const [loading, setLoading] = useState(false);
  const [orderHistory, setOrderHistory] = useState([]);
  const [activeTab, setActiveTab] = useState('order');
  const [designOptions, setDesignOptions] = useState({
    backgroundColor: '#7C3AED', // Purple from website
    textColor: '#FFFFFF',
    accentColor: '#F59E0B', // Amber from website
    font: 'Arial',
    backgroundStyle: 'gradient' // gradient, geometric, waves, dots, minimal
  });
  const [showCardSide, setShowCardSide] = useState('front'); // front or back
  const [showDesignPreview, setShowDesignPreview] = useState(false);

  const functions = getFunctions();
  const [authUser, setAuthUser] = useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setAuthUser(user);
    });
    return () => unsubscribe();
  }, []);


  // NOTE: Print ordering removed - now focusing on design generation only
  // Dimensions maintained for Gelato compatibility and professional printing
  const handlePrintOrder_removed = async (designFileUrl = null) => {
    if (!selectedProduct) {
      alert('Please select a product first');
      return;
    }

    if (!authUser) {
      alert('Please log in to place print orders');
      return;
    }

    try {
      setLoading(true);
      
      // Get product specifications from selected product
      // These mappings can be updated when a print partner is selected
      const productMapping = {
        'business-cards': 'business-card-standard',
        'flyers': 'flyer-a5-standard'
      };
      
      const productUid = productMapping[selectedProduct.category];
      
      if (!productUid) {
        alert('Product type not supported yet');
        return;
      }

      // Debug: Log the data being sent
      const requestData = {
        productUid: productUid,
        productType: selectedProduct.category,
        quantity: quantity,
        designFileUrl: designFileUrl
      };
      
      console.log('Sending request data:', requestData);
      console.log('Selected product:', selectedProduct);
      
      // This function has been removed - no external print service integration
      const response = await fetch('https://example.com/disabled-endpoint', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await authUser.getIdToken()}`
        },
        body: JSON.stringify(requestData)
      });

      if (!response.ok) {
        let errorMessage = `HTTP error! status: ${response.status}`;
        try {
          const errorData = await response.json();
          console.log('Error response:', errorData);
          errorMessage = errorData.message || errorMessage;
        } catch (parseError) {
          console.log('Could not parse error response:', parseError);
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();
      console.log('API Response:', result);

      if (result.success) {
        if (result.checkoutUrl) {
          // Redirect to Gelato checkout
          window.open(result.checkoutUrl, '_blank');
          alert('Success! You have been redirected to Gelato checkout. Complete your order there.');
        } else if (result.orderId) {
          // Order created successfully
          alert(`Order created successfully! Order ID: ${result.orderId}`);
        } else {
          alert('Order created but no checkout URL provided. Please check your Gelato dashboard.');
        }
      } else {
        throw new Error(result.message || 'Failed to create order');
      }

    } catch (error) {
      console.error('Error creating print order:', error);
      alert('Failed to create print order: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Background style renderer
  const renderBackgroundStyle = (style, backgroundColor, accentColor) => {
    switch (style) {
      case 'gradient':
        return (
          <>
            {/* Full background gradient */}
            <div style={{
              position: 'absolute',
              top: '0',
              left: '0',
              right: '0',
              bottom: '0',
              background: `linear-gradient(135deg, ${backgroundColor} 0%, ${backgroundColor}ee 70%, ${accentColor}22 100%)`,
              zIndex: 1
            }} />
            {/* Decorative elements within safe zone */}
            <div style={{
              position: 'absolute',
              top: '20px',
              right: '20px',
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              backgroundColor: accentColor,
              opacity: 0.15,
              zIndex: 2
            }} />
            <div style={{
              position: 'absolute',
              bottom: '20px',
              left: '20px',
              width: '70px',
              height: '70px',
              borderRadius: '50%',
              backgroundColor: accentColor,
              opacity: 0.12,
              zIndex: 2
            }} />
          </>
        );
      
      case 'geometric':
        return (
          <>
            {/* Solid background */}
            <div style={{
              position: 'absolute',
              top: '0',
              left: '0',
              right: '0',
              bottom: '0',
              backgroundColor: backgroundColor,
              zIndex: 1
            }} />
            {/* Geometric shapes within safe zone */}
            <div style={{
              position: 'absolute',
              top: '25px',
              right: '25px',
              width: '50px',
              height: '50px',
              backgroundColor: accentColor,
              opacity: 0.2,
              transform: 'rotate(45deg)',
              zIndex: 2
            }} />
            <div style={{
              position: 'absolute',
              bottom: '25px',
              left: '25px',
              width: '40px',
              height: '40px',
              backgroundColor: accentColor,
              opacity: 0.15,
              clipPath: 'polygon(50% 0%, 0% 100%, 100% 100%)',
              zIndex: 2
            }} />
            <div style={{
              position: 'absolute',
              top: '60px',
              left: '20px',
              width: '30px',
              height: '30px',
              backgroundColor: accentColor,
              opacity: 0.1,
              borderRadius: '50%',
              zIndex: 2
            }} />
          </>
        );
      
      case 'waves':
        return (
          <>
            {/* Background with wave pattern */}
            <div style={{
              position: 'absolute',
              top: '0',
              left: '0',
              right: '0',
              bottom: '0',
              backgroundColor: backgroundColor,
              zIndex: 1
            }} />
            {/* Wave shapes within safe zone */}
            <div style={{
              position: 'absolute',
              top: '15px',
              left: '15px',
              right: '15px',
              height: '50px',
              backgroundColor: accentColor,
              opacity: 0.1,
              borderRadius: '0 0 50% 50% / 0 0 100% 100%',
              zIndex: 2
            }} />
            <div style={{
              position: 'absolute',
              bottom: '15px',
              left: '15px',
              right: '15px',
              height: '40px',
              backgroundColor: accentColor,
              opacity: 0.15,
              borderRadius: '50% 50% 0 0 / 100% 100% 0 0',
              zIndex: 2
            }} />
          </>
        );
      
      case 'stripes':
        return (
          <>
            {/* Solid background */}
            <div style={{
              position: 'absolute',
              top: '0',
              left: '0',
              right: '0',
              bottom: '0',
              backgroundColor: backgroundColor,
              zIndex: 1
            }} />
            {/* Diagonal stripes */}
            <div style={{
              position: 'absolute',
              top: '0',
              left: '0',
              right: '0',
              bottom: '0',
              background: `repeating-linear-gradient(
                45deg,
                transparent,
                transparent 10px,
                ${accentColor}15 10px,
                ${accentColor}15 20px
              )`,
              zIndex: 2
            }} />
            {/* Additional accent stripe */}
            <div style={{
              position: 'absolute',
              top: '0',
              right: '0',
              width: '4px',
              height: '100%',
              backgroundColor: accentColor,
              opacity: 0.3,
              zIndex: 3
            }} />
          </>
        );
      
      case 'minimal':
        return (
          <>
            {/* Clean minimal background */}
            <div style={{
              position: 'absolute',
              top: '0',
              left: '0',
              right: '0',
              bottom: '0',
              backgroundColor: backgroundColor,
              zIndex: 1
            }} />
            {/* Single accent line */}
            <div style={{
              position: 'absolute',
              top: '50%',
              right: '0',
              width: '3px',
              height: '60px',
              backgroundColor: accentColor,
              opacity: 0.3,
              transform: 'translateY(-50%)',
              zIndex: 2
            }} />
          </>
        );
      
      default:
        return null;
    }
  };

  // Real QR code matching the profile page
  const QRCodeDisplay = ({ size }) => {
    const [qrDataURL, setQrDataURL] = React.useState('');
    
    React.useEffect(() => {
      const generateQR = async () => {
        try {
          const qrCodeUrl = `https://requestasong-4472b.web.app/m/${user?.uid}`;
          const dataURL = await QRCode.toDataURL(qrCodeUrl, {
            errorCorrectionLevel: 'H',
            type: 'image/png',
            quality: 0.92,
            margin: 2,
            color: {
              dark: '#000000',
              light: '#FFFFFF'
            },
            width: size * 2 // High resolution
          });
          setQrDataURL(dataURL);
        } catch (error) {
          console.error('QR code generation failed:', error);
        }
      };
      
      if (user?.uid) {
        generateQR();
      }
    }, [user?.uid, size]);
    
    return (
      <div style={{
        width: `${size}px`,
        height: `${size}px`,
        backgroundColor: 'white',
        border: '1px solid #e5e5e5',
        borderRadius: '2px',
        padding: '3px',
        margin: '0 auto',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        {qrDataURL ? (
          <img 
            src={qrDataURL} 
            alt="QR Code" 
            style={{ 
              width: '100%', 
              height: '100%', 
              objectFit: 'contain' 
            }} 
          />
        ) : (
          <div style={{ 
            color: '#666', 
            fontSize: '10px', 
            textAlign: 'center' 
          }}>
            Loading...
          </div>
        )}
      </div>
    );
  };

  useEffect(() => {
    loadAvailableProducts();
    // Order history can be re-enabled once a print partner is selected
  }, []);

  const loadAvailableProducts = async () => {
    try {
      setLoading(true);
      const getProducts = httpsCallable(functions, 'getAvailablePrintProducts');
      const result = await getProducts();
      
      if (result.data.success) {
        setProducts(result.data.products);
      }
    } catch (error) {
      console.error('Error loading products:', error);
      alert('Failed to load available products: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const loadOrderHistory = async () => {
    try {
      const getHistory = httpsCallable(functions, 'getPrintOrderHistory');
      const result = await getHistory();
      
      if (result.data.success) {
        setOrderHistory(result.data.orders);
      }
    } catch (error) {
      console.error('Error loading order history:', error);
    }
  };

  const generatePrintFiles = async () => {
    if (!selectedProduct || !displayName) {
      alert('Please complete the design first');
      return;
    }

    try {
      setLoading(true);
      
      // Generate print-ready PDF specifications
      const generatePDF = httpsCallable(functions, 'generatePrintPDF');
      const result = await generatePDF({
        productType: selectedProduct.category,
        quantity: quantity,
        designOptions: designOptions,
        displayName: displayName,
      });
      
      if (result.data.success) {
        // Store the result for display (no popup)
        // Design data generated successfully
        
        // Optional: Show brief success message
        console.log('✅ Print specifications generated successfully');
      }
    } catch (error) {
      console.error('Error generating PDF specs:', error);
      alert('Failed to generate specifications: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Generate and download JPG with QR code design
  const downloadPrintReadyPDF = async () => {
    console.log('Download button clicked');
    
    if (!selectedProduct || !displayName) {
      alert('Please complete the design first');
      return;
    }
    
    try {
      setLoading(true);
      console.log('🖼️ Generating JPG image directly in browser...');
      
      // Generate JPG image directly in the browser
      await generateAndDownloadJPG();
      
    } catch (error) {
      console.error('Error generating design file:', error);
      alert('Error generating design file: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const generateAndDownloadJPG = async () => {
    try {
      console.log('🖼️ Starting design generation...');
      
      const specs = getProductSpecs(selectedProduct.category);
      
      // Generate front design
      const frontCanvas = await createFrontDesign();
      
      // Download front side
      frontCanvas.toBlob((blob) => {
        if (blob) {
          console.log(`📁 Front JPG blob size: ${(blob.size / 1024).toFixed(1)} KB`);
          console.log(`📐 Canvas dimensions in blob: ${frontCanvas.width}px × ${frontCanvas.height}px`);
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `${displayName}-${selectedProduct.category}-FRONT.jpg`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
          console.log(`✅ Front JPG downloaded: ${link.download}`);
        }
      }, 'image/jpeg', 1.0);
      
      // Only generate back side for double-sided products
      if (specs.doubleSided) {
        const backCanvas = await createBackDesign();
        
        // Download back side after short delay
        setTimeout(() => {
          backCanvas.toBlob((blob) => {
            if (blob) {
              const url = URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = url;
              link.download = `${displayName}-${selectedProduct.category}-BACK.jpg`;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              URL.revokeObjectURL(url);
              
              console.log('✅ Both JPG files downloaded');
              
              alert(
                '✅ Business Card Design Downloaded Successfully!\n\n' +
                'Two separate print-ready JPG files have been downloaded at 300 DPI:\n\n' +
                `• ${displayName}-${selectedProduct.category}-FRONT.jpg\n` +
                `• ${displayName}-${selectedProduct.category}-BACK.jpg\n\n` +
                'These files include:\n' +
                '• Exact match to your preview design\n' +
                '• Real scannable QR code\n' +
                '• Professional bleed and crop marks\n' +
                '• High-resolution suitable for printing\n\n' +
                'Click OK to continue!'
              );
            }
          }, 'image/jpeg', 1.0);
        }, 500);
      } else {
        // Show completion alert for single-sided products
        setTimeout(() => {
          console.log('✅ Single-sided JPG file downloaded');
          
          alert(
            `✅ ${specs.name || selectedProduct.category} Design Downloaded Successfully!\n\n` +
            'One print-ready JPG file has been downloaded at 300 DPI:\n\n' +
            `• ${displayName}-${selectedProduct.category}-FRONT.jpg\n\n` +
            'This file includes:\n' +
            '• Exact match to your preview design\n' +
            '• Real scannable QR code\n' +
            '• Professional bleed and crop marks\n' +
            '• High-resolution suitable for printing\n\n' +
            'Click OK to continue!'
          );
        }, 500);
      }
      
    } catch (error) {
      console.error('❌ Error generating design:', error);
      throw error;
    }
  };

  const createFrontDesign = async () => {
    const specs = getProductSpecs(selectedProduct.category);
    // Use 300 DPI for better compatibility with design software
    // 300 DPI = 300/25.4 = 11.811 pixels per mm  
    const DPI = 300;
    const mmToPixels = DPI / 25.4;
    const bleed = specs.bleed; // Standard 3mm bleed
    const totalWidth = Math.round((specs.dimensions.width + (bleed * 2)) * mmToPixels);
    const totalHeight = Math.round((specs.dimensions.height + (bleed * 2)) * mmToPixels);
    
    console.log(`🖼️ Creating JPG for business cards:`);
    console.log(`📏 Card: ${specs.dimensions.width}mm × ${specs.dimensions.height}mm`);
    console.log(`📏 With bleed: ${specs.dimensions.width + (bleed * 2)}mm × ${specs.dimensions.height + (bleed * 2)}mm`);
    console.log(`🖥️ Canvas: ${totalWidth}px × ${totalHeight}px`);
    console.log(`📐 At ${mmToPixels.toFixed(3)} pixels/mm (${DPI} DPI)`);
    console.log(`📏 Expected dimensions: ${((specs.dimensions.width + (bleed * 2)) / 25.4).toFixed(2)}" × ${((specs.dimensions.height + (bleed * 2)) / 25.4).toFixed(2)}" at ${DPI} DPI`);
    
    const canvas = document.createElement('canvas');
    canvas.width = totalWidth;
    canvas.height = totalHeight;
    const ctx = canvas.getContext('2d');
    
    // Verify actual canvas dimensions
    console.log(`✅ Front canvas created: ${canvas.width}px × ${canvas.height}px`);
    console.log(`🔍 Verification: width=${canvas.width}, height=${canvas.height}`);
    console.log(`📐 Should show in Photoshop as: ${(canvas.width/300*25.4).toFixed(1)}mm × ${(canvas.height/300*25.4).toFixed(1)}mm at 300 DPI`);
    
    // EXACT REPLICATION OF PREVIEW LAYOUT
    // Preview dimensions vary by product type
    const isFlyer = selectedProduct.category === 'flyers';
    const isPoster = selectedProduct.category === 'posters';
    const previewWidth = isFlyer ? 320 : (isPoster ? 400 : 350);
    const scaleFactor = totalWidth / previewWidth;
    const bleedPx = bleed * mmToPixels;
    const safeZonePx = specs.safeZone * mmToPixels;
    
    // For flyers/posters, we need to handle different layout
    if (isFlyer || isPoster) {
      // Single-sided layout for flyers and posters
      ctx.fillStyle = designOptions.backgroundColor;
      ctx.fillRect(0, 0, totalWidth, totalHeight);
      await createExactBackgroundDesign(ctx, totalWidth, totalHeight);
      
      // Top accent stripe - within safe zone
      const stripeTop = isFlyer ? (40 * scaleFactor) : (30 * scaleFactor);
      const stripeLeft = isFlyer ? (40 * scaleFactor) : (30 * scaleFactor);
      const stripeRight = isFlyer ? (40 * scaleFactor) : (30 * scaleFactor);
      const stripeHeight = isFlyer ? (6 * scaleFactor) : (8 * scaleFactor);
      
      const gradient = ctx.createLinearGradient(stripeLeft, 0, totalWidth - stripeRight, 0);
      gradient.addColorStop(0, designOptions.accentColor);
      gradient.addColorStop(1, designOptions.accentColor + 'dd');
      ctx.fillStyle = gradient;
      ctx.beginPath();
      ctx.roundRect(stripeLeft, stripeTop, totalWidth - stripeLeft - stripeRight, stripeHeight, 3 * scaleFactor);
      ctx.fill();
      
      // Main content padding - within safe zone
      const contentPadding = isFlyer ? (55 * scaleFactor) : (50 * scaleFactor);
      const contentTop = isFlyer ? (65 * scaleFactor) : (60 * scaleFactor);
      
      // Header with logo
      const headerCenterX = totalWidth / 2;
      const logoSize = isFlyer ? (50 * scaleFactor) : (30 * scaleFactor);
      
      // Logo and text
      const logoImg = new Image();
      logoImg.crossOrigin = 'anonymous';
      await new Promise((resolve, reject) => {
        logoImg.onload = resolve;
        logoImg.onerror = () => {
          // Fallback circle if logo fails
          ctx.fillStyle = designOptions.accentColor;
          ctx.beginPath();
          ctx.arc(headerCenterX - logoSize - (8 * scaleFactor), contentTop + (logoSize / 2), logoSize / 2, 0, 2 * Math.PI);
          ctx.fill();
          resolve();
        };
        logoImg.src = logoSymbol;
      });
      
      if (logoImg.complete && logoImg.naturalWidth > 0) {
        // Calculate total width to center logo + text
        ctx.font = `bold ${isFlyer ? (24 * scaleFactor) : (16 * scaleFactor)}px ${designOptions.font || 'Arial'}`;
        const textWidth = ctx.measureText('Request A Song').width;
        const totalContentWidth = logoSize + (16 * scaleFactor) + textWidth;
        const startX = headerCenterX - (totalContentWidth / 2);
        
        ctx.drawImage(logoImg, startX, contentTop, logoSize, logoSize);
        
        // Main text
        ctx.fillStyle = designOptions.textColor;
        ctx.textAlign = 'left';
        ctx.textBaseline = 'top';
        ctx.shadowColor = 'rgba(0,0,0,0.2)';
        ctx.shadowBlur = 4 * scaleFactor;
        ctx.fillText('Request A Song', startX + logoSize + (16 * scaleFactor), contentTop);
        
        // Tagline
        ctx.fillStyle = designOptions.accentColor;
        ctx.font = `bold ${isFlyer ? (12 * scaleFactor) : (8 * scaleFactor)}px ${designOptions.font || 'Arial'}`;
        ctx.shadowBlur = 0;
        ctx.fillText('LIVE MUSIC PLATFORM', startX + logoSize + (16 * scaleFactor), contentTop + (isFlyer ? (26 * scaleFactor) : (18 * scaleFactor)));
      }
      
      // Artist name container
      const artistTop = contentTop + (isFlyer ? (70 * scaleFactor) : (50 * scaleFactor));
      const artistPadding = isFlyer ? (24 * scaleFactor) : (12 * scaleFactor);
      const artistHeight = isFlyer ? (44 * scaleFactor) : (30 * scaleFactor);
      
      ctx.fillStyle = 'rgba(255,255,255,0.1)';
      ctx.beginPath();
      ctx.roundRect(
        contentPadding,
        artistTop,
        totalWidth - (contentPadding * 2),
        artistHeight,
        20 * scaleFactor
      );
      ctx.fill();
      
      ctx.strokeStyle = designOptions.accentColor + '40';
      ctx.lineWidth = 2;
      ctx.stroke();
      
      ctx.fillStyle = designOptions.textColor;
      ctx.font = `bold ${isFlyer ? (20 * scaleFactor) : (14 * scaleFactor)}px ${designOptions.font || 'Arial'}`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.shadowColor = 'rgba(0,0,0,0.1)';
      ctx.shadowBlur = 2 * scaleFactor;
      ctx.fillText(displayName, headerCenterX, artistTop + (artistHeight / 2));
      
      // QR Code
      const qrTop = artistTop + artistHeight + (isFlyer ? (30 * scaleFactor) : (20 * scaleFactor));
      const qrSize = isFlyer ? (100 * scaleFactor) : (60 * scaleFactor);
      const qrPadding = 12 * scaleFactor;
      const qrContainerSize = qrSize + (qrPadding * 2);
      
      ctx.shadowColor = 'rgba(0,0,0,0.3)';
      ctx.shadowBlur = 25 * scaleFactor;
      ctx.fillStyle = 'rgba(255,255,255,0.98)';
      ctx.beginPath();
      ctx.roundRect(
        headerCenterX - (qrContainerSize / 2),
        qrTop,
        qrContainerSize,
        qrContainerSize,
        12 * scaleFactor
      );
      ctx.fill();
      
      ctx.shadowBlur = 0;
      ctx.strokeStyle = designOptions.accentColor;
      ctx.lineWidth = 3;
      ctx.stroke();
      
      // Generate QR code
      const qrCodeUrl = `https://requestasong-4472b.web.app/m/${user?.uid}`;
      const qrDataURL = await QRCode.toDataURL(qrCodeUrl, {
        errorCorrectionLevel: 'H',
        type: 'image/png',
        quality: 0.92,
        margin: 2,
        color: { dark: '#000000', light: '#FFFFFF' },
        width: Math.round(qrSize * 2)
      });
      
      const qrImage = new Image();
      await new Promise((resolve) => {
        qrImage.onload = resolve;
        qrImage.src = qrDataURL;
      });
      ctx.drawImage(qrImage, headerCenterX - (qrSize / 2), qrTop + qrPadding, qrSize, qrSize);
      
      // Instructions section
      const instructionsTop = qrTop + qrContainerSize + (isFlyer ? (30 * scaleFactor) : (20 * scaleFactor));
      
      ctx.strokeStyle = designOptions.accentColor + '40';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(contentPadding, instructionsTop);
      ctx.lineTo(totalWidth - contentPadding, instructionsTop);
      ctx.stroke();
      
      // How it works
      ctx.fillStyle = designOptions.accentColor;
      ctx.font = `bold ${isFlyer ? (12 * scaleFactor) : (16 * scaleFactor)}px ${designOptions.font || 'Arial'}`;
      ctx.textAlign = 'center';
      ctx.fillText('How it works:', headerCenterX, instructionsTop + (20 * scaleFactor));
      
      // Instructions
      ctx.fillStyle = designOptions.textColor;
      ctx.font = `${isFlyer ? (11 * scaleFactor) : (14 * scaleFactor)}px ${designOptions.font || 'Arial'}`;
      const instructions = ['1. Scan QR code', '2. Browse songs', '3. Request & pay', '4. Enjoy the show!'];
      const instructionY = instructionsTop + (40 * scaleFactor);
      const instructionSpacing = isFlyer ? (20 * scaleFactor) : (25 * scaleFactor);
      
      instructions.forEach((text, index) => {
        const x = index % 2 === 0 ? (headerCenterX - (totalWidth * 0.2)) : (headerCenterX + (totalWidth * 0.2));
        const y = instructionY + (Math.floor(index / 2) * instructionSpacing);
        ctx.textAlign = 'center';
        ctx.fillText(text, x, y);
      });
      
      // Note
      ctx.font = `${isFlyer ? (10 * scaleFactor) : (12 * scaleFactor)}px ${designOptions.font || 'Arial'}`;
      ctx.fillStyle = designOptions.textColor;
      ctx.globalAlpha = 0.8;
      ctx.textAlign = 'center';
      ctx.fillText('* You can cancel payment if song is not played', headerCenterX, instructionY + (instructionSpacing * 2.5));
      ctx.globalAlpha = 1;
      
      // Add crop marks
      addCropMarks(ctx, totalWidth, totalHeight, bleedPx);
      
      return canvas;
    }
    
    // Business card layout (existing code)
    // 1. BACKGROUND - exactly like renderBackgroundStyle
    ctx.fillStyle = designOptions.backgroundColor;
    ctx.fillRect(0, 0, totalWidth, totalHeight);
    await createExactBackgroundDesign(ctx, totalWidth, totalHeight);
    
    // 2. TOP ACCENT STRIPE - matches preview exactly (top: 23px)
    const stripeTop = 23 * scaleFactor;
    const stripeLeft = 25 * scaleFactor;
    const stripeRight = 25 * scaleFactor;
    const stripeHeight = 4 * scaleFactor;
    
    const gradient = ctx.createLinearGradient(stripeLeft, 0, totalWidth - stripeRight, 0);
    gradient.addColorStop(0, designOptions.accentColor);
    gradient.addColorStop(1, designOptions.accentColor + 'dd');
    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.roundRect(stripeLeft, stripeTop, totalWidth - stripeLeft - stripeRight, stripeHeight, 2 * scaleFactor);
    ctx.fill();
    
    // 3. MAIN CONTENT CONTAINER - matches preview padding (45px 35px 35px 35px)
    const contentTop = 45 * scaleFactor;
    const contentLeft = 35 * scaleFactor;
    const contentRight = 35 * scaleFactor;
    const contentBottom = 35 * scaleFactor;
    
    // 4. HEADER WITH LOGO - matches preview (marginTop: -15px)
    const headerTop = contentTop - (15 * scaleFactor);
    const headerCenterX = totalWidth / 2;
    
    // Request A Song logo area (centered above QR code)
    const logoSize = 35 * scaleFactor;
    
    // Calculate total width of logo + text to center them
    ctx.font = `bold ${16 * scaleFactor}px ${designOptions.font || 'Arial'}`;
    const mainTextWidth = ctx.measureText('Request A Song').width;
    const totalContentWidth = logoSize + (10 * scaleFactor) + mainTextWidth;
    const logoX = headerCenterX - (totalContentWidth / 2);
    
    // Load and draw actual RequestASong logo
    const logoImg = new Image();
    logoImg.crossOrigin = 'anonymous';
    await new Promise((resolve, reject) => {
      logoImg.onload = resolve;
      logoImg.onerror = () => {
        console.warn('Logo failed to load, using fallback');
        // Fallback circle if logo fails (centered)
        ctx.fillStyle = designOptions.accentColor;
        ctx.beginPath();
        ctx.arc(logoX + (logoSize / 2), headerTop + (logoSize / 2), logoSize / 2, 0, 2 * Math.PI);
        ctx.fill();
        resolve();
      };
      logoImg.src = logoSymbol;
    });
    
    // Draw the actual logo if it loaded
    if (logoImg.complete && logoImg.naturalWidth > 0) {
      ctx.drawImage(logoImg, logoX, headerTop, logoSize, logoSize);
    }
    
    // "Request A Song" text (centered next to logo)
    ctx.fillStyle = designOptions.textColor;
    ctx.font = `bold ${16 * scaleFactor}px ${designOptions.font || 'Arial'}`;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    ctx.shadowColor = 'rgba(0,0,0,0.2)';
    ctx.shadowBlur = 3 * scaleFactor;
    ctx.fillText('Request A Song', logoX + logoSize + (10 * scaleFactor), headerTop);
    
    // "LIVE MUSIC PLATFORM" tagline (centered under main text)
    ctx.fillStyle = designOptions.accentColor;
    ctx.font = `bold ${9 * scaleFactor}px ${designOptions.font || 'Arial'}`;
    ctx.shadowBlur = 0;
    ctx.fillText('LIVE MUSIC PLATFORM', logoX + logoSize + (10 * scaleFactor), headerTop + (18 * scaleFactor));
    
    // 5. ARTIST NAME CONTAINER - matches preview (marginBottom: 12px)
    const artistNameTop = headerTop + (35 * scaleFactor) + (8 * scaleFactor); // +8px marginBottom
    const artistNamePadding = 12 * scaleFactor;
    const artistNameHeight = 24 * scaleFactor;
    
    // Background with rounded corners (borderRadius: 15px)
    ctx.fillStyle = 'rgba(255,255,255,0.1)';
    ctx.beginPath();
    ctx.roundRect(
      headerCenterX - (120 * scaleFactor),
      artistNameTop,
      240 * scaleFactor,
      artistNameHeight,
      15 * scaleFactor
    );
    ctx.fill();
    
    // Border (1px solid accentColor30)
    ctx.strokeStyle = designOptions.accentColor + '30';
    ctx.lineWidth = 1;
    ctx.stroke();
    
    // Artist name text (fontSize: 14px, bold)
    ctx.fillStyle = designOptions.textColor;
    ctx.font = `bold ${14 * scaleFactor}px ${designOptions.font || 'Arial'}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.shadowColor = 'rgba(0,0,0,0.1)';
    ctx.shadowBlur = 2 * scaleFactor;
    ctx.fillText(displayName, headerCenterX, artistNameTop + (artistNameHeight / 2));
    
    // 6. QR CODE CONTAINER - matches preview (marginTop: -5px, size: 65)
    const qrContainerTop = artistNameTop + artistNameHeight + (12 * scaleFactor) - (5 * scaleFactor);
    const qrSize = 65 * scaleFactor;
    const qrFramePadding = 6 * scaleFactor;
    const qrFrameSize = qrSize + (qrFramePadding * 2);
    
    // QR frame background (rgba(255,255,255,0.98))
    ctx.shadowColor = 'rgba(0,0,0,0.25)';
    ctx.shadowBlur = 20 * scaleFactor;
    ctx.fillStyle = 'rgba(255,255,255,0.98)';
    ctx.beginPath();
    ctx.roundRect(
      headerCenterX - (qrFrameSize / 2),
      qrContainerTop,
      qrFrameSize,
      qrFrameSize,
      8 * scaleFactor
    );
    ctx.fill();
    
    // QR frame border (2px solid accentColor)
    ctx.shadowBlur = 0;
    ctx.strokeStyle = designOptions.accentColor;
    ctx.lineWidth = 2;
    ctx.stroke();
    
    // Generate and draw real QR code (matching profile page)
    const qrCodeUrl = `https://requestasong-4472b.web.app/m/${user?.uid}`;
    const qrDataURL = await QRCode.toDataURL(qrCodeUrl, {
      errorCorrectionLevel: 'H',
      type: 'image/png',
      quality: 0.92,
      margin: 2,
      color: {
        dark: '#000000',
        light: '#FFFFFF'
      },
      width: Math.round(qrSize * 2) // High resolution
    });
    
    const qrImage = new Image();
    await new Promise((resolve) => {
      qrImage.onload = resolve;
      qrImage.src = qrDataURL;
    });
    
    ctx.drawImage(qrImage, headerCenterX - (qrSize / 2), qrContainerTop + qrFramePadding, qrSize, qrSize);
    
    // Add crop marks for printing
    addCropMarks(ctx, totalWidth, totalHeight, bleedPx);
    
    return canvas;
  };

  const createBackDesign = async () => {
    const specs = getProductSpecs(selectedProduct.category);
    // Use 300 DPI for better compatibility with design software
    // 300 DPI = 300/25.4 = 11.811 pixels per mm  
    const DPI = 300;
    const mmToPixels = DPI / 25.4;
    const bleed = specs.bleed; // Standard 3mm bleed
    const totalWidth = Math.round((specs.dimensions.width + (bleed * 2)) * mmToPixels);
    const totalHeight = Math.round((specs.dimensions.height + (bleed * 2)) * mmToPixels);
    
    console.log(`🖼️ Creating back JPG for business cards:`);
    console.log(`📏 Card: ${specs.dimensions.width}mm × ${specs.dimensions.height}mm`);
    console.log(`📏 With bleed: ${specs.dimensions.width + (bleed * 2)}mm × ${specs.dimensions.height + (bleed * 2)}mm`);
    console.log(`🖥️ Back canvas: ${totalWidth}px × ${totalHeight}px at ${DPI} DPI`);
    
    const canvas = document.createElement('canvas');
    canvas.width = totalWidth;
    canvas.height = totalHeight;
    const ctx = canvas.getContext('2d');
    
    // Create background (same style as front)
    await createExactBackgroundDesign(ctx, totalWidth, totalHeight);
    
    // Convert preview measurements to print scale
    const scaleFactor = totalWidth / 350;
    const bleedPx = bleed * mmToPixels;
    const safeZonePx = specs.safeZone * mmToPixels;
    
    // Content area (using standard safe zone)
    const contentPadding = {
      top: bleedPx + safeZonePx,
      left: bleedPx + safeZonePx,
      right: bleedPx + safeZonePx,
      bottom: bleedPx + safeZonePx
    };
    
    const centerX = totalWidth / 2;
    const centerY = totalHeight / 2;
    
    // How It Works title (matching preview exactly)
    ctx.fillStyle = designOptions.accentColor;
    ctx.font = `bold ${16 * scaleFactor}px Arial`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('How it works:', centerX, centerY - (60 * scaleFactor));
    
    // Step 1
    const step1Y = centerY - (30 * scaleFactor);
    ctx.fillStyle = designOptions.accentColor;
    ctx.font = `bold ${12 * scaleFactor}px Arial`;
    ctx.textAlign = 'left';
    ctx.fillText('1.', contentPadding.left, step1Y);
    
    ctx.fillStyle = designOptions.textColor;
    ctx.font = `${12 * scaleFactor}px Arial`;
    ctx.fillText('Scan QR code with your phone camera', contentPadding.left + (20 * scaleFactor), step1Y);
    
    // Step 2
    const step2Y = centerY - (10 * scaleFactor);
    ctx.fillStyle = designOptions.accentColor;
    ctx.font = `bold ${12 * scaleFactor}px Arial`;
    ctx.fillText('2.', contentPadding.left, step2Y);
    
    ctx.fillStyle = designOptions.textColor;
    ctx.font = `${12 * scaleFactor}px Arial`;
    ctx.fillText('Browse available songs to request', contentPadding.left + (20 * scaleFactor), step2Y);
    
    // Step 3
    const step3Y = centerY + (10 * scaleFactor);
    ctx.fillStyle = designOptions.accentColor;
    ctx.font = `bold ${12 * scaleFactor}px Arial`;
    ctx.fillText('3.', contentPadding.left, step3Y);
    
    ctx.fillStyle = designOptions.textColor;
    ctx.font = `${12 * scaleFactor}px Arial`;
    ctx.fillText('Select and pay for your song request', contentPadding.left + (20 * scaleFactor), step3Y);
    
    // Step 4
    const step4Y = centerY + (30 * scaleFactor);
    ctx.fillStyle = designOptions.accentColor;
    ctx.font = `bold ${12 * scaleFactor}px Arial`;
    ctx.fillText('4.', contentPadding.left, step4Y);
    
    ctx.fillStyle = designOptions.textColor;
    ctx.font = `${12 * scaleFactor}px Arial`;
    ctx.fillText('Enjoy your personalized performance!', contentPadding.left + (20 * scaleFactor), step4Y);
    
    // Cancellation note (split into two lines)
    const noteY = centerY + (50 * scaleFactor);
    const note2Y = centerY + (62 * scaleFactor); // Second line with 12px spacing
    
    ctx.fillStyle = designOptions.accentColor;
    ctx.font = `bold ${10 * scaleFactor}px Arial`;
    ctx.fillText('*', contentPadding.left, noteY);
    
    ctx.fillStyle = designOptions.textColor;
    ctx.font = `${10 * scaleFactor}px Arial`;
    ctx.fillText('You can cancel payment if the song is not played', contentPadding.left + (15 * scaleFactor), noteY);
    ctx.fillText('or you\'re not satisfied', contentPadding.left + (15 * scaleFactor), note2Y);
    
    return canvas;
  };

  const createExactBackgroundDesign = async (ctx, totalWidth, totalHeight) => {
    // Fill with background color (full bleed)
    ctx.fillStyle = designOptions.backgroundColor;
    ctx.fillRect(0, 0, totalWidth, totalHeight);
    
    // Calculate scale factor for background elements
    const scaleFactor = totalWidth / 350; // Preview is 350px wide
    
    // Add background style elements based on user selection (exactly like preview)
    if (designOptions.backgroundStyle === 'gradient') {
      // Add decorative circles like the preview
      ctx.fillStyle = designOptions.accentColor + '20';
      
      // Circle at top-right
      ctx.beginPath();
      ctx.arc(totalWidth - (60 * scaleFactor), 60 * scaleFactor, 40 * scaleFactor, 0, 2 * Math.PI);
      ctx.fill();
      
      // Circle at bottom-left  
      ctx.beginPath();
      ctx.arc(60 * scaleFactor, totalHeight - (60 * scaleFactor), 25 * scaleFactor, 0, 2 * Math.PI);
      ctx.fill();
      
    } else if (designOptions.backgroundStyle === 'geometric') {
      // Add geometric shapes like the preview
      ctx.fillStyle = designOptions.accentColor + '30';
      
      // Square at top-right
      ctx.fillRect(
        totalWidth - (80 * scaleFactor), 
        30 * scaleFactor, 
        40 * scaleFactor, 
        40 * scaleFactor
      );
      
      // Triangle at bottom-left
      ctx.beginPath();
      ctx.moveTo(30 * scaleFactor, totalHeight - (60 * scaleFactor));
      ctx.lineTo(55 * scaleFactor, totalHeight - (60 * scaleFactor));
      ctx.lineTo(42.5 * scaleFactor, totalHeight - (35 * scaleFactor));
      ctx.closePath();
      ctx.fill();
      
    } else if (designOptions.backgroundStyle === 'waves') {
      // Add wave shapes like the preview
      const waveMargin = 15 * scaleFactor;
      
      // Top wave
      ctx.fillStyle = designOptions.accentColor + '15';
      ctx.beginPath();
      ctx.roundRect(
        waveMargin,
        waveMargin,
        totalWidth - (2 * waveMargin),
        50 * scaleFactor,
        25 * scaleFactor
      );
      ctx.fill();
      
      // Bottom wave
      ctx.fillStyle = designOptions.accentColor + '20';
      ctx.beginPath();
      ctx.roundRect(
        waveMargin,
        totalHeight - waveMargin - (40 * scaleFactor),
        totalWidth - (2 * waveMargin),
        40 * scaleFactor,
        20 * scaleFactor
      );
      ctx.fill();
      
    } else if (designOptions.backgroundStyle === 'stripes') {
      // Add diagonal stripes like the preview
      const stripeSpacing = 20 * scaleFactor;
      ctx.strokeStyle = designOptions.accentColor + '15';
      ctx.lineWidth = 10 * scaleFactor;
      
      for (let i = -totalHeight; i < totalWidth + totalHeight; i += stripeSpacing) {
        ctx.beginPath();
        ctx.moveTo(i, 0);
        ctx.lineTo(i + totalHeight, totalHeight);
        ctx.stroke();
      }
    }
  };

  const addCropMarks = (ctx, totalWidth, totalHeight, bleedPx) => {
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 1;
    const markLength = 15;
    const markOffset = 5;
    
    // Top-left
    ctx.beginPath();
    ctx.moveTo(0, bleedPx - markOffset);
    ctx.lineTo(markLength, bleedPx - markOffset);
    ctx.moveTo(bleedPx - markOffset, 0);
    ctx.lineTo(bleedPx - markOffset, markLength);
    ctx.stroke();
    
    // Top-right
    ctx.beginPath();
    ctx.moveTo(totalWidth - markLength, bleedPx - markOffset);
    ctx.lineTo(totalWidth, bleedPx - markOffset);
    ctx.moveTo(totalWidth - bleedPx + markOffset, 0);
    ctx.lineTo(totalWidth - bleedPx + markOffset, markLength);
    ctx.stroke();
    
    // Bottom-left
    ctx.beginPath();
    ctx.moveTo(0, totalHeight - bleedPx + markOffset);
    ctx.lineTo(markLength, totalHeight - bleedPx + markOffset);
    ctx.moveTo(bleedPx - markOffset, totalHeight - markLength);
    ctx.lineTo(bleedPx - markOffset, totalHeight);
    ctx.stroke();
    
    // Bottom-right
    ctx.beginPath();
    ctx.moveTo(totalWidth - markLength, totalHeight - bleedPx + markOffset);
    ctx.lineTo(totalWidth, totalHeight - bleedPx + markOffset);
    ctx.moveTo(totalWidth - bleedPx + markOffset, totalHeight - markLength);
    ctx.lineTo(totalWidth - bleedPx + markOffset, totalHeight);
    ctx.stroke();
  };

  const getProductSpecs = (productType) => {
    const specs = {
      'business-cards': {
        dimensions: { width: 90, height: 50, units: 'mm' },
        bleed: 3, // Standard 3mm bleed
        safeZone: 5, // 5mm safe zone from trim edge
        doubleSided: true,
        paperOptions: ['Standard (300gsm)', 'Premium (350gsm)', 'Recycled (300gsm)'],
        finishOptions: ['Matte', 'Gloss', 'Silk'],
        corners: ['Square', 'Rounded']
      },
      'flyers': {
        dimensions: { width: 148, height: 210 }, // A5
        bleed: 3,
        safeZone: 5,
        doubleSided: false,
        paperOptions: ['Standard (130gsm)', 'Premium (170gsm)', 'Thick (250gsm)'],
        finishOptions: ['Matte', 'Gloss'],
        corners: ['Square']
      },
    };
    return specs[productType] || specs['business-cards'];
  };

  // Reset card side to front when product changes, especially for single-sided products
  useEffect(() => {
    if (selectedProduct) {
      const specs = getProductSpecs(selectedProduct.category);
      if (!specs.doubleSided) {
        setShowCardSide('front');
      }
    }
  }, [selectedProduct]);

  const oldDownloadMethod = async () => {
    try {
      setLoading(true);
      console.log('Calling generatePrintPDF with:', {
        productType: selectedProduct.category,
        quantity: quantity,
        designOptions: designOptions,
        displayName: displayName,
        createActualPDF: true
      });
      
      // Generate PDF with design
      const generatePDF = httpsCallable(functions, 'generatePrintPDF');
      const result = await generatePDF({
        productType: selectedProduct.category,
        quantity: quantity,
        designOptions: designOptions,
        displayName: displayName,
        createActualPDF: true, // New flag to create actual PDF file
      });
      
      console.log('Backend response:', result.data);
      console.log('Backend pdfUrl field:', result.data.pdfUrl);
      console.log('Backend pdfUrl type:', typeof result.data.pdfUrl);
      console.log('Backend message field:', result.data.message);
      console.log('All backend fields:', Object.keys(result.data));
      
      if (result.data.success) {
        if (result.data.pdfUrl) {
          console.log('PDF URL received:', result.data.pdfUrl);
          console.log('PDF URL type:', typeof result.data.pdfUrl);
          console.log('PDF URL starts with:', result.data.pdfUrl.substring(0, 50));
          
          try {
            // Handle different file type downloads
            if (result.data.pdfUrl.startsWith('data:application/pdf')) {
              // PDF download
              const base64Data = result.data.pdfUrl.split(',')[1];
              const pdfBytes = Uint8Array.from(atob(base64Data), c => c.charCodeAt(0));
              const blob = new Blob([pdfBytes], { type: 'application/pdf' });
              const blobUrl = URL.createObjectURL(blob);
              
              // Download using blob URL
              const link = document.createElement('a');
              link.href = blobUrl;
              link.download = `${displayName}-${selectedProduct.category}-design.pdf`;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              
              // Clean up blob URL
              setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
              
              console.log('✅ PDF download initiated using blob method');
            } else if (result.data.pdfUrl.startsWith('data:text/plain')) {
              // Text specification download
              const base64Data = result.data.pdfUrl.split(',')[1];
              const textContent = atob(base64Data);
              const blob = new Blob([textContent], { type: 'text/plain' });
              const blobUrl = URL.createObjectURL(blob);
              
              // Download using blob URL
              const link = document.createElement('a');
              link.href = blobUrl;
              link.download = `${displayName}-${selectedProduct.category}-design-specs.txt`;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              
              // Clean up blob URL
              setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
              
              console.log('✅ Text specifications download initiated');
            } else if (result.data.pdfUrl.startsWith('data:image/svg+xml')) {
              // SVG download (fallback)
              const base64Data = result.data.pdfUrl.split(',')[1];
              const svgContent = atob(base64Data);
              const blob = new Blob([svgContent], { type: 'image/svg+xml' });
              const blobUrl = URL.createObjectURL(blob);
              
              // Download using blob URL
              const link = document.createElement('a');
              link.href = blobUrl;
              link.download = `${displayName}-${selectedProduct.category}-design.svg`;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              
              // Clean up blob URL
              setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
              
              console.log('✅ SVG download initiated using blob method (fallback)');
            } else {
              // Direct URL download
              const link = document.createElement('a');  
              link.href = result.data.pdfUrl;
              link.download = `${displayName}-${selectedProduct.category}-design.pdf`;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              
              console.log('✅ Download initiated using direct data URL');
            }
          } catch (downloadError) {
            console.error('❌ Download error:', downloadError);
            alert('Download failed. Please check the console for details.');
            return;
          }
          
          // Show appropriate message based on file type
          if (result.data.pdfUrl.startsWith('data:text/plain')) {
            alert(
              '✅ Design Specifications Downloaded!\n\n' +
              'Your comprehensive design specifications have been downloaded as a text file.\n\n' +
              'This file contains:\n' +
              '• Complete design details (colors, layout, dimensions)\n' +
              '• QR code information and URL\n' +
              '• Professional print specifications\n' +
              '• Technical requirements for printers\n\n' +
              'Take this file to any graphic designer or print shop - they can create the actual design file from these detailed specifications!'
            );
          } else {
            alert(
              '✅ Design File Downloaded Successfully!\n\n' +
              'Your print-ready file has been downloaded and is ready for printing!\n\n' +
              'Next steps:\n' +
              '• Upload this file to your preferred print service\n' +
              '• Take it to any local print shop\n' +
              '• File includes proper specifications for professional printing\n\n' +
              'Your design is print-ready!'
            );
          }
        } else {
          console.log('No PDF URL in response, showing specs instead');
          // Show the specifications in a downloadable format
          const specs = result.data.pdfInstructions || result.data;
          const specsText = JSON.stringify(specs, null, 2);
          const blob = new Blob([specsText], { type: 'text/plain' });
          const url = URL.createObjectURL(blob);
          
          const link = document.createElement('a');
          link.href = url;
          link.download = `${displayName}-${selectedProduct.category}-specs.txt`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
          
          alert(
            '📄 Design Specifications Downloaded!\n\n' +
            'Your design specifications have been saved as a text file.\n' +
            'Use these specs to create your design in any design software.'
          );
        }
      } else {
        console.error('Backend returned success: false');
        alert('Failed to generate design file. Please try again.');
      }
    } catch (error) {
      console.error('Error generating PDF:', error);
      console.error('Error details:', error.code, error.message, error.details);
      alert('Failed to generate PDF: ' + (error.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const downloadPDFInstructions = (quoteData) => {
    console.log('Download PDF clicked, quote data:', quoteData);
    
    if (!quoteData) {
      alert('No design data available. Please generate a design first.');
      return;
    }
    
    // Check if we have proper instructions, if not create a basic version
    let instructions, specs;
    
    if (quoteData.pdfInstructions) {
      instructions = quoteData.pdfInstructions;
      specs = quoteData.specs;
    } else {
      // Fallback: create basic instructions from available data
      specs = quoteData.specs || selectedProduct;
      
      instructions = {
        fileSpecs: {
          format: 'PDF/X-4',
          colorProfile: 'GRACoL 2006', 
          resolution: '300 DPI',
          totalSize: `${(specs.dimensions?.width || 90) + 8}mm × ${(specs.dimensions?.height || 50) + 8}mm`,
          trimSize: `${specs.dimensions?.width || 90}mm × ${specs.dimensions?.height || 50}mm`
        },
        designLayout: {
          qrCode: {
            position: 'Center-left',
            size: '40mm',
            url: `https://platform.requestasong.com.au/${displayName}`,
            margin: '5mm from edges'
          },
          text: {
            title: displayName,
            subtitle: 'Request A Song',
            tagline: 'Live Music Platform',
            font: 'Arial or similar sans-serif',
            minSize: '7pt',
            color: designOptions.textColor
          },
          background: {
            style: designOptions.backgroundStyle,
            primaryColor: designOptions.backgroundColor,
            accentColor: designOptions.accentColor
          }
        },
        technicalNotes: [
          'Export as PDF/X-4 with embedded fonts',
          'Use CMYK color mode for best results', 
          'Include 4mm bleed on all sides',
          'Keep important elements 4mm from trim line',
          'Flatten all layers and transparencies'
        ]
      };
    }

    // Create downloadable PDF instructions document
    const content = `PRINT SPECIFICATIONS & INSTRUCTIONS
${displayName} - ${specs.name}

=== PRODUCT SPECIFICATIONS ===
Size: ${specs.dimensions.width}mm × ${specs.dimensions.height}mm
Bleed: ${specs.bleed}mm all sides
Safe Area: ${specs.safeArea}mm from trim line
Minimum Quantity: ${specs.minQuantity}

=== FILE REQUIREMENTS ===
Format: ${instructions.fileSpecs.format}
Color Profile: ${instructions.fileSpecs.colorProfile}
Resolution: ${instructions.fileSpecs.resolution}
Total Size (with bleed): ${instructions.fileSpecs.totalSize}
Trim Size: ${instructions.fileSpecs.trimSize}

=== DESIGN LAYOUT ===
QR Code:
- Position: ${instructions.designLayout.qrCode.position}
- Size: ${instructions.designLayout.qrCode.size}
- URL: ${instructions.designLayout.qrCode.url}
- Margin: ${instructions.designLayout.qrCode.margin}

Text Elements:
- Title: "${instructions.designLayout.text.title}"
- Subtitle: "${instructions.designLayout.text.subtitle}"
- Tagline: "${instructions.designLayout.text.tagline}"
- Font: ${instructions.designLayout.text.font}
- Minimum Size: ${instructions.designLayout.text.minSize}
- Color: ${instructions.designLayout.text.color}

Background:
- Style: ${instructions.designLayout.background.style}
- Primary Color: ${instructions.designLayout.background.primaryColor}
- Accent Color: ${instructions.designLayout.background.accentColor}

=== TECHNICAL NOTES ===
${instructions.technicalNotes.map((note, i) => `${i + 1}. ${note}`).join('\n')}

=== PRINTING OPTIONS ===
Paper Options: ${specs.paperOptions.join(', ')}
Finish Options: ${specs.finishOptions.join(', ')}
Corners: ${specs.corners.join(', ')}
${specs.doubleSided ? 'Double-sided: Yes' : 'Double-sided: No'}

Generated: ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}
Platform: Request A Song (https://platform.requestasong.com.au)
`;

    // Download as text file
    const blob = new Blob([content], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${displayName}-print-specifications.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const getQuote = async () => {
    if (!selectedProduct) {
      alert('Please select a product first');
      return;
    }

    try {
      setLoading(true);
      const getPrintQuote = httpsCallable(functions, 'getPrintQuote');
      const result = await getPrintQuote({
        productUid: selectedProduct.uid,
        quantity: quantity,
        productType: selectedProduct.category
      });
      
      if (result.data.success) {
        // Design data generated successfully
      }
    } catch (error) {
      console.error('Error getting quote:', error);
      alert('Failed to get quote: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const ProductCard = ({ product, isSelected, onClick }) => {
    const getProductIcon = (category) => {
      switch (category) {
        case 'business-cards': return '💳';
        case 'flyers': return '📋';
        default: return '📄';
      }
    };

    const getProductImage = (category) => {
      // Mock product images - in real implementation, these would be actual product photos
      const baseStyle = {
        width: '100%',
        height: '80px',
        borderRadius: '8px',
        marginBottom: '12px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '32px',
        fontWeight: 'bold',
        position: 'relative'
      };

      const dimensionStyle = {
        position: 'absolute',
        bottom: '4px',
        right: '4px',
        backgroundColor: 'rgba(0,0,0,0.7)',
        color: 'white',
        fontSize: '10px',
        padding: '2px 6px',
        borderRadius: '4px'
      };

      switch (category) {
        case 'business-cards':
          return (
            <div style={{...baseStyle, background: 'linear-gradient(135deg, #7C3AED 0%, #4F46E5 100%)', color: 'white'}}>
              💳
              <div style={dimensionStyle}>90×50mm</div>
            </div>
          );
        case 'flyers':
          return (
            <div style={{...baseStyle, background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)', color: 'white'}}>
              📋
              <div style={dimensionStyle}>A5</div>
            </div>
          );
        default:
          return <div style={{...baseStyle, background: '#374151', color: 'white'}}>📄</div>;
      }
    };

    return (
      <div
        onClick={onClick}
        style={{
          border: `3px solid ${isSelected ? '#7C3AED' : '#4B5563'}`,
          borderRadius: '12px',
          padding: '16px',
          cursor: 'pointer',
          backgroundColor: isSelected ? '#7C3AED' : '#374151',
          color: 'white',
          transition: 'all 0.3s ease',
          position: 'relative',
          overflow: 'hidden',
          boxShadow: isSelected 
            ? '0 4px 6px rgba(124, 58, 237, 0.3)' 
            : '0 2px 4px rgba(0, 0, 0, 0.2)',
          transform: isSelected ? 'scale(1.02)' : 'scale(1)'
        }}
        onMouseEnter={(e) => {
          if (!isSelected) {
            e.currentTarget.style.transform = 'scale(1.05)';
            e.currentTarget.style.boxShadow = '0 6px 10px rgba(124, 58, 237, 0.2)';
            e.currentTarget.style.borderColor = '#7C3AED';
          }
        }}
        onMouseLeave={(e) => {
          if (!isSelected) {
            e.currentTarget.style.transform = 'scale(1)';
            e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.2)';
            e.currentTarget.style.borderColor = '#4B5563';
          }
        }}
      >
        {getProductImage(product.category)}
        
        <h4 style={{ margin: '0 0 8px 0', fontSize: '16px', fontWeight: 'bold' }}>
          {product.name}
        </h4>
        <p style={{ margin: '0 0 12px 0', fontSize: '14px', opacity: 0.8 }}>
          {product.description}
        </p>
        
        {/* Add a button-like element */}
        <div style={{
          marginTop: '12px',
          padding: '8px 16px',
          backgroundColor: isSelected ? 'white' : '#7C3AED',
          color: isSelected ? '#7C3AED' : 'white',
          borderRadius: '8px',
          textAlign: 'center',
          fontWeight: 'bold',
          fontSize: '14px',
          border: isSelected ? '2px solid #7C3AED' : 'none'
        }}>
          {isSelected ? '✓ Selected' : 'Select This Product'}
        </div>
        
        {isSelected && (
          <div style={{
            position: 'absolute',
            top: '8px',
            right: '8px',
            backgroundColor: '#10B981',
            color: 'white',
            borderRadius: '50%',
            width: '24px',
            height: '24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '12px'
          }}>
            ✓
          </div>
        )}
      </div>
    );
  };

  const OrderHistoryItem = ({ order }) => (
    <div style={{
      backgroundColor: '#374151',
      borderRadius: '8px',
      padding: '16px',
      marginBottom: '12px',
      border: '2px solid #4B5563'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
        <h4 style={{ margin: 0, color: 'white', fontSize: '16px' }}>
          Order #{order.orderId || 'N/A'}
        </h4>
        <span style={{
          backgroundColor: order.status === 'completed' ? '#10B981' : 
                          order.status === 'processing' ? '#F59E0B' : '#6B7280',
          color: 'white',
          padding: '4px 8px',
          borderRadius: '4px',
          fontSize: '12px',
          fontWeight: 'bold'
        }}>
          {order.status}
        </span>
      </div>
      <div style={{ color: '#C4B5FD', fontSize: '14px' }}>
        <div>Items: {order.items?.length || 0}</div>
        <div>Total: {order.currency} {order.totalCost}</div>
        <div>Ordered: {new Date(order.createdAt).toLocaleDateString()}</div>
      </div>
    </div>
  );

  return (
    <div style={{
      backgroundColor: '#1F2937',
      border: '4px solid #7C3AED',
      borderRadius: '16px',
      overflow: 'hidden',
      marginBottom: '24px'
    }}>
      {/* Header */}
      <div style={{
        padding: '24px',
        borderBottom: '4px solid #6D28D9',
        backgroundColor: '#581C87'
      }}>
        <h3 style={{
          fontSize: '20px',
          fontWeight: 'bold',
          color: 'white',
          margin: 0,
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <Printer size={24} />
          Print QR Code Materials
        </h3>
        <p style={{ color: '#C4B5FD', margin: '8px 0 0 0', fontSize: '14px' }}>
          Order professional printed materials with your QR code
        </p>
      </div>

      {/* Tab Navigation */}
      <div style={{ backgroundColor: '#374151', padding: '16px' }}>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          {[
            { id: 'order', label: 'Order Materials', icon: Printer }
          ].map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  backgroundColor: activeTab === tab.id ? '#7C3AED' : 'transparent',
                  color: activeTab === tab.id ? 'white' : '#9CA3AF',
                  border: `2px solid ${activeTab === tab.id ? '#7C3AED' : '#4B5563'}`,
                  borderRadius: '8px',
                  padding: '8px 16px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '14px',
                  fontWeight: 'bold'
                }}
              >
                <Icon size={16} />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      <div style={{ padding: '24px' }}>
        {/* Setup Status Message */}
        {activeTab === 'order' && (
          <div style={{
            backgroundColor: '#1E3A8A',
            border: '2px solid #3B82F6',
            borderRadius: '12px',
            padding: '16px',
            marginBottom: '24px',
            color: '#BFDBFE'
          }}>
            <h4 style={{ margin: '0 0 8px 0', color: '#93C5FD' }}>🎨 Design Assistant</h4>
            <p style={{ margin: '0 0 12px 0', fontSize: '14px' }}>
              Professional print design made simple:
            </p>
            <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '14px' }}>
              <li>✅ Professional design templates with your QR code</li>
              <li>✅ Business cards and flyers available</li>
              <li>✅ Download high-quality JPG files</li>
              <li>✅ Use with any print service of your choice</li>
            </ul>
            <p style={{ margin: '12px 0 0 0', fontSize: '12px', opacity: 0.8 }}>
              💡 Design your materials here, then download the files to print anywhere!
            </p>
          </div>
        )}

        {activeTab === 'order' && (
          <div>
            {/* Product Selection */}
            <div style={{ marginBottom: '24px' }}>
              <h4 style={{ color: 'white', marginBottom: '16px' }}>Select Product Type</h4>
              
              {/* Business Cards */}
              {products.businessCards.length > 0 && (
                <div style={{ marginBottom: '20px' }}>
                  <h5 style={{ color: '#C4B5FD', marginBottom: '12px' }}>📇 Business Cards</h5>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '12px' }}>
                    {products.businessCards.map(product => (
                      <ProductCard
                        key={product.uid}
                        product={product}
                        isSelected={selectedProduct?.uid === product.uid}
                        onClick={() => {
                          setSelectedProduct(product);
                        }}
                      />
                    ))}
                  </div>
                  
                  {/* Show design options immediately after business card selection */}
                  {selectedProduct && selectedProduct.category === 'business-cards' && (
                    <div style={{ marginTop: '24px' }}>
                      {/* Design Customization Section */}
                      <div style={{ 
                        backgroundColor: '#374151',
                        borderRadius: '12px',
                        padding: '20px',
                        marginBottom: '24px',
                        border: '2px solid #4B5563'
                      }}>
                        <h4 style={{ color: 'white', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <Palette size={20} />
                          Customize Your Design
                        </h4>
                        
                        {/* Background Style Selector */}
                        <div style={{ marginBottom: '20px' }}>
                          <label style={{ display: 'block', color: '#C4B5FD', marginBottom: '12px', fontSize: '14px' }}>
                            Background Style
                          </label>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: '8px' }}>
                            {[
                              { value: 'gradient', name: 'Gradient', preview: 'linear-gradient(135deg, #7C3AED 0%, #F59E0B 100%)' },
                              { value: 'geometric', name: 'Geometric', preview: '#7C3AED' },
                              { value: 'waves', name: 'Waves', preview: 'linear-gradient(180deg, #7C3AED 0%, #059669 100%)' },
                              { value: 'minimal', name: 'Minimal', preview: '#374151' }
                            ].map(style => (
                              <button
                                key={style.value}
                                onClick={() => setDesignOptions({...designOptions, backgroundStyle: style.value})}
                                style={{
                                  height: '50px',
                                  background: style.preview,
                                  border: designOptions.backgroundStyle === style.value ? '3px solid white' : '2px solid #4B5563',
                                  borderRadius: '8px',
                                  cursor: 'pointer',
                                  position: 'relative',
                                  overflow: 'hidden'
                                }}
                              >
                                <div style={{
                                  position: 'absolute',
                                  bottom: '2px',
                                  left: '0',
                                  right: '0',
                                  backgroundColor: 'rgba(0,0,0,0.7)',
                                  color: 'white',
                                  fontSize: '10px',
                                  fontWeight: 'bold',
                                  padding: '2px',
                                  textAlign: 'center'
                                }}>
                                  {style.name}
                                </div>
                              </button>
                            ))}
                          </div>
                        </div>
                        
                        {/* Color Options */}
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px' }}>
                          <div>
                            <label style={{ display: 'block', color: '#C4B5FD', marginBottom: '6px', fontSize: '12px' }}>
                              Primary Color
                            </label>
                            <input
                              type="color"
                              value={designOptions.backgroundColor}
                              onChange={(e) => setDesignOptions({...designOptions, backgroundColor: e.target.value})}
                              style={{
                                width: '100%',
                                height: '40px',
                                borderRadius: '8px',
                                border: '2px solid #4B5563',
                                cursor: 'pointer'
                              }}
                            />
                          </div>
                          <div>
                            <label style={{ display: 'block', color: '#C4B5FD', marginBottom: '6px', fontSize: '12px' }}>
                              Text Color
                            </label>
                            <input
                              type="color"
                              value={designOptions.textColor}
                              onChange={(e) => setDesignOptions({...designOptions, textColor: e.target.value})}
                              style={{
                                width: '100%',
                                height: '40px',
                                borderRadius: '8px',
                                border: '2px solid #4B5563',
                                cursor: 'pointer'
                              }}
                            />
                          </div>
                        </div>
                      </div>

                      {/* Design Preview Section for Business Cards */}
                      <div style={{ 
                        backgroundColor: '#1F2937',
                        borderRadius: '12px',
                        padding: '20px',
                        marginBottom: '24px',
                        border: '2px solid #4B5563'
                      }}>
                        <h4 style={{ color: 'white', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <Eye size={20} />
                          Live Preview
                        </h4>

                        {/* Preview controls for double-sided products */}
                        {selectedProduct && getProductSpecs(selectedProduct.category).doubleSided && (
                          <div style={{ 
                            backgroundColor: '#374151',
                            borderRadius: '8px',
                            padding: '12px',
                            marginBottom: '16px',
                            border: '1px solid #4B5563'
                          }}>
                            <div style={{ color: '#60A5FA', fontSize: '12px', fontWeight: 'bold', marginBottom: '8px' }}>
                              👀 View Both Sides
                            </div>
                            <div style={{ display: 'flex', gap: '8px' }}>
                              <button
                                onClick={() => setShowCardSide('front')}
                                style={{
                                  backgroundColor: showCardSide === 'front' ? designOptions.backgroundColor : 'transparent',
                                  color: showCardSide === 'front' ? 'white' : '#9CA3AF',
                                  border: 'none',
                                  padding: '8px 16px',
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                  fontSize: '14px',
                                  fontWeight: 'bold'
                                }}
                              >
                                Front Side
                              </button>
                              <button
                                onClick={() => setShowCardSide('back')}
                                style={{
                                  backgroundColor: showCardSide === 'back' ? designOptions.backgroundColor : 'transparent',
                                  color: showCardSide === 'back' ? 'white' : '#9CA3AF',
                                  border: 'none',
                                  padding: '8px 16px',
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                  fontSize: '14px',
                                  fontWeight: 'bold'
                                }}
                              >
                                Back Side
                              </button>
                            </div>
                          </div>
                        )}

                        {/* Print Bleed Information */}
                        <div style={{ 
                          backgroundColor: '#374151',
                          borderRadius: '8px',
                          padding: '12px',
                          marginBottom: '16px',
                          border: '1px solid #4B5563'
                        }}>
                          <div style={{ color: '#60A5FA', fontSize: '12px', fontWeight: 'bold', marginBottom: '4px' }}>
                            📐 Print Specifications
                          </div>
                          <div style={{ color: '#9CA3AF', fontSize: '11px', lineHeight: '1.4' }}>
                            Background extends to edges (bleed area). Important content stays within the dashed safe zone to prevent trimming during professional printing.
                          </div>
                        </div>

                        {/* Business Card Preview */}
                        <div style={{ 
                          width: '350px', 
                          height: '220px',
                          margin: '0 auto',
                          backgroundColor: designOptions.backgroundColor,
                          borderRadius: '12px',
                          border: '2px solid #4B5563',
                          position: 'relative',
                          boxShadow: '0 8px 25px rgba(0,0,0,0.3)',
                          overflow: 'hidden'
                        }}>
                          {/* Print Guidelines - Safe Zone Indicator */}
                          <div style={{
                            position: 'absolute',
                            top: '15px',
                            left: '15px',
                            right: '15px',
                            bottom: '15px',
                            border: '1px dashed rgba(255,255,255,0.3)',
                            borderRadius: '6px',
                            pointerEvents: 'none',
                            zIndex: 10
                          }} />
                          <div style={{
                            position: 'absolute',
                            top: '4px',
                            right: '4px',
                            backgroundColor: 'rgba(0,0,0,0.6)',
                            color: 'white',
                            padding: '2px 6px',
                            borderRadius: '3px',
                            fontSize: '9px',
                            fontWeight: 'bold',
                            zIndex: 11
                          }}>SAFE ZONE</div>
                          {(!selectedProduct || !getProductSpecs(selectedProduct.category).doubleSided || showCardSide === 'front') ? (
                            // Front side - Full bleed design with artist name
                            <div style={{
                              height: '100%',
                              position: 'relative'
                            }}>
                              {/* Dynamic background style */}
                              {renderBackgroundStyle(designOptions.backgroundStyle, designOptions.backgroundColor, designOptions.accentColor)}

                              {/* Top accent stripe - moved up by 50% of height */}
                              <div style={{
                                position: 'absolute',
                                top: '23px', // Was 25px, moved up by 2px (50% of 4px height)
                                left: '25px',
                                right: '25px',
                                height: '4px',
                                background: `linear-gradient(90deg, ${designOptions.accentColor} 0%, ${designOptions.accentColor}dd 100%)`,
                                borderRadius: '2px',
                                zIndex: 4
                              }} />

                              {/* Main content */}
                              <div style={{
                                position: 'relative',
                                zIndex: 3,
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                padding: '45px 35px 35px 35px' // Extra safe zone padding
                              }}>
                                {/* Header with logo - moved up significantly */}
                                <div style={{ 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  justifyContent: 'center',
                                  marginBottom: '8px',
                                  marginTop: '-15px', // Was 8px, moved up by 23px (75% of ~30px height)
                                  textAlign: 'center'
                                }}>
                                  <RequestASongLogo size={35} symbolOnly={true} />
                                  <div style={{ marginLeft: '10px' }}>
                                    <div style={{
                                      color: designOptions.textColor,
                                      fontFamily: designOptions.font,
                                      fontSize: '16px',
                                      fontWeight: 'bold',
                                      lineHeight: '1.1',
                                      textShadow: '0 1px 3px rgba(0,0,0,0.2)'
                                    }}>
                                      Request A Song
                                    </div>
                                    <div style={{
                                      color: designOptions.accentColor,
                                      fontFamily: designOptions.font,
                                      fontSize: '9px',
                                      fontWeight: 'bold',
                                      textTransform: 'uppercase',
                                      letterSpacing: '0.5px'
                                    }}>
                                      Live Music Platform
                                    </div>
                                  </div>
                                </div>

                                {/* Artist Name */}
                                <div style={{
                                  textAlign: 'center',
                                  marginBottom: '12px',
                                  backgroundColor: 'rgba(255,255,255,0.1)',
                                  padding: '6px 12px',
                                  borderRadius: '15px',
                                  border: `1px solid ${designOptions.accentColor}30`
                                }}>
                                  <div style={{
                                    color: designOptions.textColor,
                                    fontFamily: designOptions.font,
                                    fontSize: '14px',
                                    fontWeight: 'bold',
                                    textShadow: '0 1px 2px rgba(0,0,0,0.1)'
                                  }}>
                                    {displayName || user?.displayName || user?.email?.split('@')[0] || 'Artist Name'}
                                  </div>
                                </div>

                                {/* QR Code with enhanced frame - moved up */}
                                <div style={{ 
                                  flex: 1, 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  justifyContent: 'center',
                                  marginBottom: '20px',
                                  marginTop: '-5px' // Was 8px, moved up by 13px (20% of ~65px)
                                }}>
                                  <div style={{
                                    padding: '6px',
                                    backgroundColor: 'rgba(255,255,255,0.98)',
                                    borderRadius: '8px',
                                    boxShadow: '0 6px 20px rgba(0,0,0,0.25)',
                                    border: `2px solid ${designOptions.accentColor}`,
                                    position: 'relative'
                                  }}>
                                    <QRCodeDisplay size={65} />
                                  </div>
                                </div>
                                
                              </div>

                            </div>
                          ) : (
                            // Back side - Instructions and info
                            <div style={{
                              height: '100%',
                              display: 'flex',
                              flexDirection: 'column',
                              justifyContent: 'center',
                              padding: '45px 35px' // Extra safe zone padding for back side
                            }}>

                              {/* How it works section */}
                              <div style={{ marginBottom: '16px' }}>
                                <h3 style={{
                                  color: designOptions.textColor,
                                  fontFamily: designOptions.font,
                                  fontSize: '16px',
                                  fontWeight: 'bold',
                                  margin: '0 0 12px 0',
                                  textAlign: 'center'
                                }}>
                                  How It Works
                                </h3>
                                
                                <div style={{
                                  color: designOptions.textColor,
                                  fontFamily: designOptions.font,
                                  fontSize: '12px',
                                  lineHeight: '1.4'
                                }}>
                                  <div style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <span style={{ color: designOptions.accentColor, fontWeight: 'bold' }}>1.</span>
                                    <span>Scan QR code with your phone camera</span>
                                  </div>
                                  <div style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <span style={{ color: designOptions.accentColor, fontWeight: 'bold' }}>2.</span>
                                    <span>Browse available songs to request</span>
                                  </div>
                                  <div style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <span style={{ color: designOptions.accentColor, fontWeight: 'bold' }}>3.</span>
                                    <span>Select and pay for your song request</span>
                                  </div>
                                  <div style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <span style={{ color: designOptions.accentColor, fontWeight: 'bold' }}>4.</span>
                                    <span>Enjoy your personalized performance!</span>
                                  </div>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <span style={{ color: designOptions.accentColor, fontWeight: 'bold' }}>*</span>
                                    <span>You can cancel payment if the song is not played or you're not satisfied</span>
                                  </div>
                                </div>
                              </div>

                            </div>
                          )}
                        </div>
                      </div>

                      {/* Download Section for Business Cards */}
                      {displayName && (
                        <div style={{ marginBottom: '24px', textAlign: 'center' }}>
                          <button
                            onClick={downloadPrintReadyPDF}
                            disabled={loading}
                            style={{
                              backgroundColor: loading ? '#6B7280' : '#059669',
                              color: 'white',
                              border: 'none',
                              borderRadius: '12px',
                              padding: '16px 32px',
                              fontSize: '18px',
                              fontWeight: 'bold',
                              cursor: loading ? 'not-allowed' : 'pointer',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '12px',
                              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                            }}
                          >
                            {loading ? <Clock size={20} /> : <Download size={20} />}
                            {loading ? 'Generating...' : '🖼️ Download Print-Ready JPG'}
                          </button>
                          
                          <p style={{ color: '#9CA3AF', marginTop: '12px', fontSize: '14px' }}>
                            Get high-resolution JPG files with your QR code design, ready for any print service
                          </p>
                        </div>
                      )}

                    </div>
                  )}
                </div>
              )}

              {/* Flyers */}
              {products.flyers.length > 0 && (
                <div style={{ marginBottom: '20px' }}>
                  <h5 style={{ color: '#C4B5FD', marginBottom: '12px' }}>📋 Flyers</h5>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '12px' }}>
                    {products.flyers.map(product => (
                      <ProductCard
                        key={product.uid}
                        product={product}
                        isSelected={selectedProduct?.uid === product.uid}
                        onClick={() => {
                          setSelectedProduct(product);
                        }}
                      />
                    ))}
                  </div>
                  
                  {/* Show design options immediately after flyer selection */}
                  {selectedProduct && selectedProduct.category === 'flyers' && (
                    <div style={{ marginTop: '24px' }}>
                      {/* Design Customization Section for Flyers */}
                      <div style={{ 
                        backgroundColor: '#374151',
                        borderRadius: '12px',
                        padding: '20px',
                        marginBottom: '24px',
                        border: '2px solid #4B5563'
                      }}>
                        <h4 style={{ color: 'white', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <Palette size={20} />
                          Customize Your Design
                        </h4>
                        
                        {/* Background Style Selector */}
                        <div style={{ marginBottom: '20px' }}>
                          <label style={{ display: 'block', color: '#C4B5FD', marginBottom: '12px', fontSize: '14px' }}>
                            Background Style
                          </label>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: '8px' }}>
                            {[
                              { value: 'gradient', name: 'Gradient', preview: 'linear-gradient(135deg, #7C3AED 0%, #F59E0B 100%)' },
                              { value: 'geometric', name: 'Geometric', preview: '#7C3AED' },
                              { value: 'waves', name: 'Waves', preview: 'linear-gradient(180deg, #7C3AED 0%, #059669 100%)' },
                              { value: 'minimal', name: 'Minimal', preview: '#374151' }
                            ].map(style => (
                              <button
                                key={style.value}
                                onClick={() => setDesignOptions({...designOptions, backgroundStyle: style.value})}
                                style={{
                                  height: '50px',
                                  background: style.preview,
                                  border: designOptions.backgroundStyle === style.value ? '3px solid white' : '2px solid #4B5563',
                                  borderRadius: '8px',
                                  cursor: 'pointer',
                                  position: 'relative',
                                  overflow: 'hidden'
                                }}
                              >
                                <div style={{
                                  position: 'absolute',
                                  bottom: '2px',
                                  left: '0',
                                  right: '0',
                                  backgroundColor: 'rgba(0,0,0,0.7)',
                                  color: 'white',
                                  fontSize: '10px',
                                  fontWeight: 'bold',
                                  padding: '2px',
                                  textAlign: 'center'
                                }}>
                                  {style.name}
                                </div>
                              </button>
                            ))}
                          </div>
                        </div>
                        
                        {/* Color Options */}
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px' }}>
                          <div>
                            <label style={{ display: 'block', color: '#C4B5FD', marginBottom: '6px', fontSize: '12px' }}>
                              Primary Color
                            </label>
                            <input
                              type="color"
                              value={designOptions.backgroundColor}
                              onChange={(e) => setDesignOptions({...designOptions, backgroundColor: e.target.value})}
                              style={{
                                width: '100%',
                                height: '40px',
                                borderRadius: '8px',
                                border: '2px solid #4B5563',
                                cursor: 'pointer'
                              }}
                            />
                          </div>
                          <div>
                            <label style={{ display: 'block', color: '#C4B5FD', marginBottom: '6px', fontSize: '12px' }}>
                              Text Color
                            </label>
                            <input
                              type="color"
                              value={designOptions.textColor}
                              onChange={(e) => setDesignOptions({...designOptions, textColor: e.target.value})}
                              style={{
                                width: '100%',
                                height: '40px',
                                borderRadius: '8px',
                                border: '2px solid #4B5563',
                                cursor: 'pointer'
                              }}
                            />
                          </div>
                        </div>
                      </div>

                      {/* Design Preview Section for Flyers */}
                      <div style={{ 
                        backgroundColor: '#1F2937',
                        borderRadius: '12px',
                        padding: '20px',
                        marginBottom: '24px',
                        border: '2px solid #4B5563'
                      }}>
                        <h4 style={{ color: 'white', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <Eye size={20} />
                          Live Preview
                        </h4>

                        {/* Print Bleed Information */}
                        <div style={{ 
                          backgroundColor: '#374151',
                          borderRadius: '8px',
                          padding: '12px',
                          marginBottom: '16px',
                          border: '1px solid #4B5563'
                        }}>
                          <div style={{ color: '#60A5FA', fontSize: '12px', fontWeight: 'bold', marginBottom: '4px' }}>
                            📐 Print Specifications
                          </div>
                          <div style={{ color: '#9CA3AF', fontSize: '11px', lineHeight: '1.4' }}>
                            Background extends to edges (bleed area). Important content stays within the dashed safe zone to prevent trimming during professional printing.
                          </div>
                        </div>

                        {/* Flyer Preview */}
                        <div style={{ 
                          width: selectedProduct.category === 'flyers' ? '320px' : '400px',
                          height: selectedProduct.category === 'flyers' ? '450px' : '550px',
                          margin: '0 auto',
                          backgroundColor: designOptions.backgroundColor,
                          borderRadius: '12px',
                          border: '2px solid #4B5563',
                          position: 'relative',
                          boxShadow: '0 8px 25px rgba(0,0,0,0.3)',
                          overflow: 'hidden'
                        }}>
                          {/* Safe Zone Indicator */}
                          <div style={{
                            position: 'absolute',
                            top: '20px',
                            left: '20px',
                            right: '20px',
                            bottom: '20px',
                            border: '1px dashed rgba(255,255,255,0.3)',
                            borderRadius: '6px',
                            pointerEvents: 'none',
                            zIndex: 10
                          }} />
                          <div style={{
                            position: 'absolute',
                            top: '4px',
                            right: '4px',
                            backgroundColor: 'rgba(0,0,0,0.6)',
                            color: 'white',
                            padding: '2px 6px',
                            borderRadius: '3px',
                            fontSize: '9px',
                            fontWeight: 'bold',
                            zIndex: 11
                          }}>SAFE ZONE</div>

                          {/* Flyer Content with background style */}
                          <div style={{
                            height: '100%',
                            position: 'relative'
                          }}>
                            {/* Dynamic background style */}
                            {renderBackgroundStyle(designOptions.backgroundStyle, designOptions.backgroundColor, designOptions.accentColor)}

                            {/* Top accent stripe - moved higher to avoid text collision */}
                            <div style={{
                              position: 'absolute',
                              top: selectedProduct.category === 'flyers' ? '30px' : '30px', // Moved up from 40px to 30px for flyers
                              left: selectedProduct.category === 'flyers' ? '40px' : '30px',
                              right: selectedProduct.category === 'flyers' ? '40px' : '30px',
                              height: selectedProduct.category === 'flyers' ? '6px' : '8px',
                              background: `linear-gradient(90deg, ${designOptions.accentColor} 0%, ${designOptions.accentColor}dd 100%)`,
                              borderRadius: '3px',
                              zIndex: 4
                            }} />

                            {/* Main content - within safe zone */}
                            <div style={{
                              position: 'relative',
                              zIndex: 3,
                              height: '100%'
                            }}>
                              {(() => {
                                // MOVED ALL ELEMENTS UP FOR BETTER SPACING
                                const isFlyer = selectedProduct.category === 'flyers';
                                const contentPadding = isFlyer ? 55 : 50;
                                const contentTop = isFlyer ? 40 : 60; // Moved header up more (was 50)
                                const logoSize = isFlyer ? 40 : 30; // Slightly smaller logo
                                
                                // Artist container calculations - moved up
                                const artistTop = contentTop + (isFlyer ? 80 : 50); // Reduced spacing (was 90)
                                const artistHeight = isFlyer ? 40 : 30; // Slightly smaller height
                                
                                // QR Code calculations - moved up
                                const qrTop = artistTop + artistHeight + (isFlyer ? 30 : 20); // Reduced spacing (was 35)
                                const qrSize = isFlyer ? 80 : 60; // 20% smaller: 100px * 0.8 = 80px
                                const qrPadding = 12;
                                const qrContainerSize = qrSize + (qrPadding * 2);
                                
                                // Instructions calculations - moved up to fit better
                                const instructionsTop = qrTop + qrContainerSize + (isFlyer ? 20 : 20); // Reduced spacing (was 25)
                                
                                return (
                                  <>
                                    {/* Header with logo - exact position from canvas */}
                                    <div style={{
                                      position: 'absolute',
                                      top: `${contentTop}px`,
                                      left: '50%',
                                      transform: 'translateX(-50%)',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center'
                                    }}>
                                      <RequestASongLogo size={logoSize} symbolOnly={true} />
                                      <div style={{ marginLeft: '16px' }}>
                                        <div style={{
                                          color: designOptions.textColor,
                                          fontFamily: designOptions.font,
                                          fontSize: isFlyer ? '19px' : '16px', // 20% smaller: 24px * 0.8 = 19px
                                          fontWeight: 'bold',
                                          lineHeight: '1.1',
                                          textShadow: '0 2px 4px rgba(0,0,0,0.2)'
                                        }}>
                                          Request A Song
                                        </div>
                                        <div style={{
                                          color: designOptions.accentColor,
                                          fontFamily: designOptions.font,
                                          fontSize: isFlyer ? '10px' : '8px', // 20% smaller: 12px * 0.8 = 10px
                                          fontWeight: 'bold',
                                          textTransform: 'uppercase',
                                          letterSpacing: '1px',
                                          marginTop: isFlyer ? '2px' : '1px'
                                        }}>
                                          Live Music Platform
                                        </div>
                                      </div>
                                    </div>
                                    
                                    {/* Artist Name Container - exact position from canvas */}
                                    <div style={{
                                      position: 'absolute',
                                      top: `${artistTop}px`,
                                      left: `${contentPadding}px`,
                                      right: `${contentPadding}px`,
                                      height: `${artistHeight}px`,
                                      backgroundColor: 'rgba(255,255,255,0.1)',
                                      borderRadius: '20px',
                                      border: `2px solid ${designOptions.accentColor}40`,
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center'
                                    }}>
                                      <div style={{
                                        color: designOptions.textColor,
                                        fontFamily: designOptions.font,
                                        fontSize: isFlyer ? '16px' : '14px', // 20% smaller: 20px * 0.8 = 16px
                                        fontWeight: 'bold',
                                        textShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                      }}>
                                        {displayName || user?.displayName || user?.email?.split('@')[0] || 'Artist Name'}
                                      </div>
                                    </div>
                                    
                                    {/* QR Code Section - exact position from canvas */}
                                    <div style={{
                                      position: 'absolute',
                                      top: `${qrTop}px`,
                                      left: '50%',
                                      transform: 'translateX(-50%)',
                                      width: `${qrContainerSize}px`,
                                      height: `${qrContainerSize}px`,
                                      padding: `${qrPadding}px`,
                                      backgroundColor: 'rgba(255,255,255,0.98)',
                                      borderRadius: '12px',
                                      boxShadow: '0 8px 25px rgba(0,0,0,0.3)',
                                      border: `3px solid ${designOptions.accentColor}`,
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center'
                                    }}>
                                      <QRCodeDisplay size={qrSize} />
                                    </div>
                                    
                                    {/* Instructions Section - positioned within safe zone */}
                                    <div style={{
                                      position: 'absolute',
                                      top: `${instructionsTop}px`,
                                      left: `${contentPadding}px`,
                                      right: `${contentPadding}px`,
                                      borderTop: `2px solid ${designOptions.accentColor}40`,
                                      paddingTop: '15px',
                                      fontSize: isFlyer ? '10px' : '14px', // Slightly smaller for flyers
                                      color: designOptions.textColor,
                                      fontFamily: designOptions.font,
                                      lineHeight: '1.4', // Tighter line height
                                      textAlign: 'center'
                                    }}>
                                      <div style={{ 
                                        fontWeight: 'bold', 
                                        marginBottom: '6px', // Less margin
                                        color: designOptions.accentColor, 
                                        fontSize: isFlyer ? '11px' : '16px' // Slightly smaller title
                                      }}>
                                        How it works:
                                      </div>
                                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                                        <div>1. Scan QR code</div>
                                        <div>2. Browse songs</div>
                                        <div>3. Request & pay</div>
                                        <div>4. Enjoy the show!</div>
                                      </div>
                                      <div style={{ 
                                        marginTop: '6px', // Less margin
                                        fontSize: isFlyer ? '9px' : '12px', // Smaller disclaimer text
                                        opacity: 0.8 
                                      }}>
                                        * You can cancel payment if song is not played
                                      </div>
                                    </div>
                                  </>
                                );
                              })()}

                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Download Section for Flyers */}
                      {displayName && (
                        <div style={{ marginBottom: '24px', textAlign: 'center' }}>
                          <button
                            onClick={downloadPrintReadyPDF}
                            disabled={loading}
                            style={{
                              backgroundColor: loading ? '#6B7280' : '#059669',
                              color: 'white',
                              border: 'none',
                              borderRadius: '12px',
                              padding: '16px 32px',
                              fontSize: '18px',
                              fontWeight: 'bold',
                              cursor: loading ? 'not-allowed' : 'pointer',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '12px',
                              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                            }}
                          >
                            {loading ? <Clock size={20} /> : <Download size={20} />}
                            {loading ? 'Generating...' : '🖼️ Download Print-Ready JPG'}
                          </button>
                          
                          <p style={{ color: '#9CA3AF', marginTop: '12px', fontSize: '14px' }}>
                            Get high-resolution JPG files with your QR code design, ready for any print service
                          </p>
                        </div>
                      )}

                    </div>
                  )}
                </div>
              )}

            </div>

            {/* Show global design options only when no specific product is selected - REMOVED */}
            {false && selectedProduct && (
              <>
                {/* Design Customization Section */}
                <div style={{ 
                  backgroundColor: '#374151',
                  borderRadius: '12px',
                  padding: '20px',
                  marginBottom: '24px',
                  border: '2px solid #4B5563'
                }}>
                  <h4 style={{ color: 'white', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Palette size={20} />
                    Customize Your Design
                  </h4>
                  
                  {/* Background Style Selector */}
                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', color: '#C4B5FD', marginBottom: '12px', fontSize: '14px' }}>
                      Background Style
                    </label>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: '8px' }}>
                      {[
                        { value: 'gradient', name: 'Gradient', preview: 'linear-gradient(135deg, #7C3AED 0%, #F59E0B 100%)' },
                        { value: 'geometric', name: 'Geometric', preview: '#7C3AED' },
                        { value: 'waves', name: 'Waves', preview: 'linear-gradient(180deg, #7C3AED 0%, #059669 100%)' },
                        { value: 'minimal', name: 'Minimal', preview: '#374151' }
                      ].map(style => (
                        <button
                          key={style.value}
                          onClick={() => setDesignOptions({...designOptions, backgroundStyle: style.value})}
                          style={{
                            height: '50px',
                            background: style.preview,
                            border: designOptions.backgroundStyle === style.value ? '3px solid white' : '2px solid #4B5563',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            position: 'relative',
                            overflow: 'hidden'
                          }}
                        >
                          <div style={{
                            position: 'absolute',
                            bottom: '2px',
                            left: '0',
                            right: '0',
                            backgroundColor: 'rgba(0,0,0,0.7)',
                            color: 'white',
                            fontSize: '10px',
                            fontWeight: 'bold',
                            padding: '2px',
                            textAlign: 'center'
                          }}>
                            {style.name}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  {/* Color Options */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px' }}>
                    <div>
                      <label style={{ display: 'block', color: '#C4B5FD', marginBottom: '6px', fontSize: '12px' }}>
                        Primary Color
                      </label>
                      <input
                        type="color"
                        value={designOptions.backgroundColor}
                        onChange={(e) => setDesignOptions({...designOptions, backgroundColor: e.target.value})}
                        style={{
                          width: '100%',
                          height: '40px',
                          borderRadius: '8px',
                          border: '2px solid #4B5563',
                          cursor: 'pointer'
                        }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', color: '#C4B5FD', marginBottom: '6px', fontSize: '12px' }}>
                        Text Color
                      </label>
                      <input
                        type="color"
                        value={designOptions.textColor}
                        onChange={(e) => setDesignOptions({...designOptions, textColor: e.target.value})}
                        style={{
                          width: '100%',
                          height: '40px',
                          borderRadius: '8px',
                          border: '2px solid #4B5563',
                          cursor: 'pointer'
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Design Preview Section */}
                <div style={{ 
                  backgroundColor: '#1F2937',
                  borderRadius: '12px',
                  padding: '20px',
                  marginBottom: '24px',
                  border: '2px solid #4B5563'
                }}>
                  <h4 style={{ color: 'white', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Eye size={20} />
                    Live Preview
                  </h4>

                  {/* Preview controls for double-sided products */}
                  {selectedProduct && getProductSpecs(selectedProduct.category).doubleSided && (
                    <div style={{ 
                      backgroundColor: '#374151',
                      borderRadius: '8px',
                      padding: '12px',
                      marginBottom: '16px',
                      border: '1px solid #4B5563'
                    }}>
                      <div style={{ color: '#60A5FA', fontSize: '12px', fontWeight: 'bold', marginBottom: '8px' }}>
                        👀 View Both Sides
                      </div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          onClick={() => setShowCardSide('front')}
                          style={{
                            backgroundColor: showCardSide === 'front' ? designOptions.backgroundColor : 'transparent',
                            color: showCardSide === 'front' ? 'white' : '#9CA3AF',
                            border: 'none',
                            padding: '8px 16px',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: 'bold'
                          }}
                        >
                          Front Side
                        </button>
                        <button
                          onClick={() => setShowCardSide('back')}
                          style={{
                            backgroundColor: showCardSide === 'back' ? designOptions.backgroundColor : 'transparent',
                            color: showCardSide === 'back' ? 'white' : '#9CA3AF',
                            border: 'none',
                            padding: '8px 16px',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: 'bold'
                          }}
                        >
                          Back Side
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Print Bleed Information */}
                  <div style={{ 
                    backgroundColor: '#374151',
                    borderRadius: '8px',
                    padding: '12px',
                    marginBottom: '16px',
                    border: '1px solid #4B5563'
                  }}>
                    <div style={{ color: '#60A5FA', fontSize: '12px', fontWeight: 'bold', marginBottom: '4px' }}>
                      📐 Print Specifications
                    </div>
                    <div style={{ color: '#9CA3AF', fontSize: '11px', lineHeight: '1.4' }}>
                      Background extends to edges (bleed area). Important content stays within the dashed safe zone to prevent trimming during professional printing.
                    </div>
                  </div>

                  {/* Live Preview */}
                  {selectedProduct && getProductSpecs(selectedProduct.category).doubleSided ? (
                    // Double-sided design for business cards - Dynamic sizing
                    <div style={{ 
                      width: `${Math.round(getProductSpecs(selectedProduct.category).dimensions.width * 3.5)}px`,
                      height: `${Math.round(getProductSpecs(selectedProduct.category).dimensions.height * 3.5)}px`,
                      margin: '0 auto',
                      backgroundColor: designOptions.backgroundColor,
                      borderRadius: '12px',
                      border: '2px solid #4B5563',
                      position: 'relative',
                      boxShadow: '0 8px 25px rgba(0,0,0,0.3)',
                      overflow: 'hidden'
                    }}>
                      {/* Print Guidelines - Safe Zone Indicator */}
                      <div style={{
                        position: 'absolute',
                        top: '15px',
                        left: '15px',
                        right: '15px',
                        bottom: '15px',
                        border: '1px dashed rgba(255,255,255,0.3)',
                        borderRadius: '6px',
                        pointerEvents: 'none',
                        zIndex: 10
                      }} />
                      <div style={{
                        position: 'absolute',
                        top: '4px',
                        right: '4px',
                        backgroundColor: 'rgba(0,0,0,0.6)',
                        color: 'white',
                        padding: '2px 6px',
                        borderRadius: '3px',
                        fontSize: '9px',
                        fontWeight: 'bold',
                        zIndex: 11
                      }}>SAFE ZONE</div>
                      {(!selectedProduct || !getProductSpecs(selectedProduct.category).doubleSided || showCardSide === 'front') ? (
                        // Front side - Full bleed design with artist name
                        <div style={{
                          height: '100%',
                          position: 'relative'
                        }}>
                          {/* Dynamic background style */}
                          {renderBackgroundStyle(designOptions.backgroundStyle, designOptions.backgroundColor, designOptions.accentColor)}

                          {/* Top accent stripe - moved up by 50% of height */}
                          <div style={{
                            position: 'absolute',
                            top: '23px', // Was 25px, moved up by 2px (50% of 4px height)
                            left: '25px',
                            right: '25px',
                            height: '4px',
                            background: `linear-gradient(90deg, ${designOptions.accentColor} 0%, ${designOptions.accentColor}dd 100%)`,
                            borderRadius: '2px',
                            zIndex: 4
                          }} />

                          {/* Main content */}
                          <div style={{
                            position: 'relative',
                            zIndex: 3,
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            padding: '45px 35px 35px 35px' // Extra safe zone padding
                          }}>
                            {/* Header with logo - moved up significantly */}
                            <div style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              justifyContent: 'center',
                              marginBottom: '8px',
                              marginTop: '-15px', // Was 8px, moved up by 23px (75% of ~30px height)
                              textAlign: 'center'
                            }}>
                              <RequestASongLogo size={35} symbolOnly={true} />
                              <div style={{ marginLeft: '10px' }}>
                                <div style={{
                                  color: designOptions.textColor,
                                  fontFamily: designOptions.font,
                                  fontSize: '16px',
                                  fontWeight: 'bold',
                                  lineHeight: '1.1',
                                  textShadow: '0 1px 3px rgba(0,0,0,0.2)'
                                }}>
                                  Request A Song
                                </div>
                                <div style={{
                                  color: designOptions.accentColor,
                                  fontFamily: designOptions.font,
                                  fontSize: '9px',
                                  fontWeight: 'bold',
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.5px'
                                }}>
                                  Live Music Platform
                                </div>
                              </div>
                            </div>

                            {/* Artist Name */}
                            <div style={{
                              textAlign: 'center',
                              marginBottom: '12px',
                              backgroundColor: 'rgba(255,255,255,0.1)',
                              padding: '6px 12px',
                              borderRadius: '15px',
                              border: `1px solid ${designOptions.accentColor}30`
                            }}>
                              <div style={{
                                color: designOptions.textColor,
                                fontFamily: designOptions.font,
                                fontSize: '14px',
                                fontWeight: 'bold',
                                textShadow: '0 1px 2px rgba(0,0,0,0.1)'
                              }}>
                                {displayName || user?.displayName || user?.email?.split('@')[0] || 'Artist Name'}
                              </div>
                            </div>

                            {/* QR Code with enhanced frame - moved up */}
                            <div style={{ 
                              flex: 1, 
                              display: 'flex', 
                              alignItems: 'center', 
                              justifyContent: 'center',
                              marginBottom: '20px',
                              marginTop: '-5px' // Was 8px, moved up by 13px (20% of ~65px)
                            }}>
                              <div style={{
                                padding: '6px',
                                backgroundColor: 'rgba(255,255,255,0.98)',
                                borderRadius: '8px',
                                boxShadow: '0 6px 20px rgba(0,0,0,0.25)',
                                border: `2px solid ${designOptions.accentColor}`,
                                position: 'relative'
                              }}>
                                <QRCodeDisplay size={65} />
                              </div>
                            </div>
                            
                          </div>

                        </div>
                      ) : (
                        // Back side - Instructions and info
                        <div style={{
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          padding: '45px 35px' // Extra safe zone padding for back side
                        }}>

                          {/* How it works section */}
                          <div style={{ marginBottom: '16px' }}>
                            <h3 style={{
                              color: designOptions.textColor,
                              fontFamily: designOptions.font,
                              fontSize: '16px',
                              fontWeight: 'bold',
                              margin: '0 0 12px 0',
                              textAlign: 'center'
                            }}>
                              How It Works
                            </h3>
                            
                            <div style={{
                              color: designOptions.textColor,
                              fontFamily: designOptions.font,
                              fontSize: '12px',
                              lineHeight: '1.4'
                            }}>
                              <div style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span style={{ color: designOptions.accentColor, fontWeight: 'bold' }}>1.</span>
                                <span>Scan QR code with your phone camera</span>
                              </div>
                              <div style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span style={{ color: designOptions.accentColor, fontWeight: 'bold' }}>2.</span>
                                <span>Browse available songs to request</span>
                              </div>
                              <div style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span style={{ color: designOptions.accentColor, fontWeight: 'bold' }}>3.</span>
                                <span>Select and pay for your song request</span>
                              </div>
                              <div style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span style={{ color: designOptions.accentColor, fontWeight: 'bold' }}>4.</span>
                                <span>Enjoy your personalized performance!</span>
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span style={{ color: designOptions.accentColor, fontWeight: 'bold' }}>*</span>
                                <span>You can cancel payment if the song is not played or you're not satisfied</span>
                              </div>
                            </div>
                          </div>

                        </div>
                      )}

                    </div>
                  ) : (
                    // Single-sided design for flyers and posters with safe zones
                    <div style={{ 
                      width: selectedProduct.category === 'flyers' ? '320px' : '400px',
                      height: selectedProduct.category === 'flyers' ? '450px' : '550px',
                      margin: '0 auto',
                      backgroundColor: designOptions.backgroundColor,
                      borderRadius: '12px',
                      border: '2px solid #4B5563',
                      position: 'relative',
                      boxShadow: '0 8px 25px rgba(0,0,0,0.3)',
                      overflow: 'hidden'
                    }}>
                      {/* Safe Zone Indicator */}
                      <div style={{
                        position: 'absolute',
                        top: '20px',
                        left: '20px',
                        right: '20px',
                        bottom: '20px',
                        border: '1px dashed rgba(255,255,255,0.3)',
                        borderRadius: '6px',
                        pointerEvents: 'none',
                        zIndex: 10
                      }} />
                      <div style={{
                        position: 'absolute',
                        top: '4px',
                        right: '4px',
                        backgroundColor: 'rgba(0,0,0,0.6)',
                        color: 'white',
                        padding: '2px 6px',
                        borderRadius: '3px',
                        fontSize: '9px',
                        fontWeight: 'bold',
                        zIndex: 11
                      }}>SAFE ZONE</div>

                      {/* Flyer/Poster Content with background style */}
                      <div style={{
                        height: '100%',
                        position: 'relative'
                      }}>
                        {/* Dynamic background style */}
                        {renderBackgroundStyle(designOptions.backgroundStyle, designOptions.backgroundColor, designOptions.accentColor)}

                        {/* Top accent stripe - moved higher to avoid text collision */}
                        <div style={{
                          position: 'absolute',
                          top: selectedProduct.category === 'flyers' ? '30px' : '30px', // Moved up from 40px to 30px for flyers
                          left: selectedProduct.category === 'flyers' ? '40px' : '30px',
                          right: selectedProduct.category === 'flyers' ? '40px' : '30px',
                          height: selectedProduct.category === 'flyers' ? '6px' : '8px',
                          background: `linear-gradient(90deg, ${designOptions.accentColor} 0%, ${designOptions.accentColor}dd 100%)`,
                          borderRadius: '3px',
                          zIndex: 4
                        }} />

                        {/* Main content - within safe zone */}
                        <div style={{
                          position: 'relative',
                          zIndex: 3,
                          height: '100%'
                        }}>
                          {(() => {
                            // MOVED ALL ELEMENTS UP FOR BETTER SPACING
                            const isFlyer = selectedProduct.category === 'flyers';
                            const contentPadding = isFlyer ? 55 : 50;
                            const contentTop = isFlyer ? 40 : 60; // Moved header up more (was 50)
                            const logoSize = isFlyer ? 40 : 30; // Slightly smaller logo
                            
                            // Artist container calculations - moved up
                            const artistTop = contentTop + (isFlyer ? 80 : 50); // Reduced spacing (was 90)
                            const artistHeight = isFlyer ? 40 : 30; // Slightly smaller height
                            
                            // QR Code calculations - moved up
                            const qrTop = artistTop + artistHeight + (isFlyer ? 30 : 20); // Reduced spacing (was 35)
                            const qrSize = isFlyer ? 80 : 60; // 20% smaller: 100px * 0.8 = 80px
                            const qrPadding = 12;
                            const qrContainerSize = qrSize + (qrPadding * 2);
                            
                            // Instructions calculations - moved up to fit better
                            const instructionsTop = qrTop + qrContainerSize + (isFlyer ? 20 : 20); // Reduced spacing (was 25)
                            
                            return (
                              <>
                                {/* Header with logo - exact position from canvas */}
                                <div style={{
                                  position: 'absolute',
                                  top: `${contentTop}px`,
                                  left: '50%',
                                  transform: 'translateX(-50%)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}>
                                  <RequestASongLogo size={logoSize} symbolOnly={true} />
                                  <div style={{ marginLeft: '16px' }}>
                                    <div style={{
                                      color: designOptions.textColor,
                                      fontFamily: designOptions.font,
                                      fontSize: isFlyer ? '19px' : '16px', // 20% smaller: 24px * 0.8 = 19px
                                      fontWeight: 'bold',
                                      lineHeight: '1.1',
                                      textShadow: '0 2px 4px rgba(0,0,0,0.2)'
                                    }}>
                                      Request A Song
                                    </div>
                                    <div style={{
                                      color: designOptions.accentColor,
                                      fontFamily: designOptions.font,
                                      fontSize: isFlyer ? '10px' : '8px', // 20% smaller: 12px * 0.8 = 10px
                                      fontWeight: 'bold',
                                      textTransform: 'uppercase',
                                      letterSpacing: '1px',
                                      marginTop: isFlyer ? '2px' : '1px'
                                    }}>
                                      Live Music Platform
                                    </div>
                                  </div>
                                </div>
                                
                                {/* Artist Name Container - exact position from canvas */}
                                <div style={{
                                  position: 'absolute',
                                  top: `${artistTop}px`,
                                  left: `${contentPadding}px`,
                                  right: `${contentPadding}px`,
                                  height: `${artistHeight}px`,
                                  backgroundColor: 'rgba(255,255,255,0.1)',
                                  borderRadius: '20px',
                                  border: `2px solid ${designOptions.accentColor}40`,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}>
                                  <div style={{
                                    color: designOptions.textColor,
                                    fontFamily: designOptions.font,
                                    fontSize: isFlyer ? '16px' : '14px', // 20% smaller: 20px * 0.8 = 16px
                                    fontWeight: 'bold',
                                    textShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                  }}>
                                    {displayName || user?.displayName || user?.email?.split('@')[0] || 'Artist Name'}
                                  </div>
                                </div>
                                
                                {/* QR Code Section - exact position from canvas */}
                                <div style={{
                                  position: 'absolute',
                                  top: `${qrTop}px`,
                                  left: '50%',
                                  transform: 'translateX(-50%)',
                                  width: `${qrContainerSize}px`,
                                  height: `${qrContainerSize}px`,
                                  padding: `${qrPadding}px`,
                                  backgroundColor: 'rgba(255,255,255,0.98)',
                                  borderRadius: '12px',
                                  boxShadow: '0 8px 25px rgba(0,0,0,0.3)',
                                  border: `3px solid ${designOptions.accentColor}`,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}>
                                  <QRCodeDisplay size={qrSize} />
                                </div>
                                
                                {/* Instructions Section - positioned within safe zone */}
                                <div style={{
                                  position: 'absolute',
                                  top: `${instructionsTop}px`,
                                  left: `${contentPadding}px`,
                                  right: `${contentPadding}px`,
                                  borderTop: `2px solid ${designOptions.accentColor}40`,
                                  paddingTop: '15px',
                                  fontSize: isFlyer ? '10px' : '14px', // Slightly smaller for flyers
                                  color: designOptions.textColor,
                                  fontFamily: designOptions.font,
                                  lineHeight: '1.4', // Tighter line height
                                  textAlign: 'center'
                                }}>
                                  <div style={{ 
                                    fontWeight: 'bold', 
                                    marginBottom: '6px', // Less margin
                                    color: designOptions.accentColor, 
                                    fontSize: isFlyer ? '11px' : '16px' // Slightly smaller title
                                  }}>
                                    How it works:
                                  </div>
                                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                                    <div>1. Scan QR code</div>
                                    <div>2. Browse songs</div>
                                    <div>3. Request & pay</div>
                                    <div>4. Enjoy the show!</div>
                                  </div>
                                  <div style={{ 
                                    marginTop: '6px', // Less margin
                                    fontSize: isFlyer ? '9px' : '12px', // Smaller disclaimer text
                                    opacity: 0.8 
                                  }}>
                                    * You can cancel payment if song is not played
                                  </div>
                                </div>
                              </>
                            );
                          })()}

                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}


            {/* Print Information & Recommendations */}
            {selectedProduct && (
              <div style={{
                backgroundColor: '#065F46',
                border: '2px solid #10B981',
                borderRadius: '12px',
                padding: '20px',
                marginBottom: '24px'
              }}>
                <h4 style={{ color: 'white', margin: '0 0 16px 0' }}>Print Information & Recommendations</h4>
                
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px' }}>
                  {/* Product Specs */}
                  <div style={{ backgroundColor: '#064E3B', padding: '16px', borderRadius: '8px' }}>
                    <h5 style={{ color: '#A7F3D0', margin: '0 0 12px 0' }}>📄 Design Details</h5>
                    <div style={{ color: '#D1FAE5', fontSize: '14px' }}>
                      <div>Product: {selectedProduct.name}</div>
                      <div>Size: {selectedProduct.dimensions}</div>
                      <div>Format: High-quality JPG</div>
                      <div>Category: {selectedProduct.category}</div>
                    </div>
                  </div>

                  {/* File Specifications */}
                  <div style={{ backgroundColor: '#064E3B', padding: '16px', borderRadius: '8px' }}>
                    <h5 style={{ color: '#A7F3D0', margin: '0 0 12px 0' }}>🖨️ File Specifications</h5>
                    <div style={{ color: '#D1FAE5', fontSize: '14px' }}>
                      <div>Resolution: 300 DPI</div>
                      <div>Bleed: 3mm included</div>
                      <div>Quality: Print-ready</div>
                      <div>Compatibility: All printers</div>
                    </div>
                  </div>
                </div>

                {/* Printing Recommendation */}
                <div style={{ 
                  backgroundColor: '#1E3A8A', 
                  border: '2px solid #3B82F6',
                  borderRadius: '12px',
                  padding: '20px',
                  marginTop: '20px'
                }}>
                  <h5 style={{ color: '#93C5FD', margin: '0 0 12px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    🌟 Recommended Print Service
                  </h5>
                  <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr auto', gap: '16px', alignItems: 'center' }}>
                    <div style={{ color: '#BFDBFE', fontSize: '14px', lineHeight: '1.5' }}>
                      <strong style={{ color: '#93C5FD' }}>Gelato</strong> offers professional printing with dimensions that perfectly match our business card and flyer designs. 
                      They provide high-quality printing with global delivery and competitive pricing.
                    </div>
                    <div></div>
                    <a 
                      href="https://www.gelato.com/au" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      style={{
                        backgroundColor: '#7C3AED',
                        color: 'white',
                        textDecoration: 'none',
                        padding: '10px 16px',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: 'bold',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      Visit Gelato
                      <ExternalLink size={14} />
                    </a>
                  </div>
                  <p style={{ color: '#9CA3AF', fontSize: '12px', marginTop: '8px', margin: '8px 0 0 0' }}>
                    💡 Of course, you can use any print service you prefer - our files work everywhere!
                  </p>
                </div>

              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
};

export default PrintOrderInterface;